import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import {
  AlertCircle,
  ArrowDown,
  ArrowUp,
  ChevronLeft,
  CalendarPlus,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ChevronsUpDown,
  CalendarDays,
  CaseSensitive,
  Clock,
  Columns3,
  Hash,
  Link2,
  ToggleLeft,
  Download,
  EllipsisVertical,
  Eye,
  ListFilter,
  Pencil,
  RotateCw,
  Search,
  Trash2,
  UserPen,
  UserRound,
  Upload,
  X,
  type LucideIcon,
} from "lucide-react";
import {
  type FormEvent,
  type ReactNode,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "sonner";

import { readImage } from "@/components/avatar";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ConfirmDialog, Modal } from "@/components/ui/dialog";
import { EmptyState } from "@/components/ui/empty-state";
import { menuItemClass, MenuSeparator } from "@/components/ui/menu";
import {
  BackArrow,
  backLinkClass,
  FilterChip,
  Page,
  PageHeader,
  Toolbar,
} from "@/components/ui/page";
import { SearchInput } from "@/components/ui/search-input";
import { type LoadingAction, LoadingNote } from "@/components/ui/loading";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox, Field, Input, Textarea, Toggle } from "@/components/ui/field";
import { FkSelect, fetchCompact, labelsKey } from "@/components/ui/fk-select";
import { OptionSelect } from "@/components/ui/option-select";
import { Select } from "@/components/ui/select";
import { api, errorMessage } from "@/services/api";
import { decodeRecordId, encodeRecordId } from "@/utils/record-token";
import { useLoadMore } from "@/hooks/use-load-more";
import { useTranslation } from "@/store/locale";
import {
  type ListParams,
  createResource,
  deleteResource,
  listCursor,
  listResource,
  queryResource,
  updateResource,
} from "@/utils/geo";
import { can } from "@/utils/permissions";
import { profileQuery } from "@/services/queries";
import type { Profile } from "@/meta/types";
import { useDelayed } from "@/hooks/use-delayed";
import { cn } from "@/utils/utils";

/**
 * One list-and-edit page for every master-data resource.
 *
 * The API already does the work — search, filters, a sort allowlist, paging —
 * so this only has to carry state to the query string and lay the result out.
 * Configuration is data: columns, sortable fields, filters, and the form.
 */

export interface Option {
  value: string | number;
  label: string;
}

export interface FieldDef {
  name: string;
  label: string;
  type?:
    | "text"
    | "number"
    | "select"
    | "fk"
    | "date"
    | "boolean"
    | "image"
    | "file"
    | "textarea"
    | "email"
    | "tel";
  required?: boolean;
  /** Group heading in the form; fields with the same section sit under one heading. */
  section?: string;
  /** Not editable once the row exists. */
  lockOnEdit?: boolean;
  disabled?: boolean;
  step?: string;
  /** Numbers for a number field; `YYYY-MM-DD` for a date one. */
  min?: number | string;
  max?: number | string;
  /** `fk` only: the /compact endpoint; `parentParam` names the query key `dependsOn` fills. */
  endpoint?: string;
  parentParam?: string;
  /** Static choices, or a loader keyed by the value of `dependsOn`. */
  options?: Option[];
  loadOptions?: (parent: string, signal?: AbortSignal) => Promise<Option[]>;
  dependsOn?: string;
}

export interface FilterDef {
  name: string;
  label: string;
  type: "number" | "select" | "fk" | "boolean" | "date" | "text";
  /** `fk` only: the /compact endpoint; `parentParam` names the query key `dependsOn` fills. */
  endpoint?: string;
  parentParam?: string;
  options?: Option[];
  /** Static choices, or a loader; with `dependsOn`, keyed by that filter's value. */
  loadOptions?: (parent: string, signal?: AbortSignal) => Promise<Option[]>;
  dependsOn?: string;
}

/** What a bespoke detail view is given besides the row. */
export interface DetailContext {
  onClose: () => void;
  lookup: Lookup;
  onRefresh: () => void;
  busy: boolean;
  /** Absent when the screen or the person may not do it. */
  onEdit?: (() => void) | undefined;
  onDelete?: (() => void) | undefined;
}

/** Labels for ids, keyed by the filter whose options supplied them. */
export type Lookup = (filter: string, value: string | number | null) => ReactNode;

export interface ColumnDef<T> {
  key: string;
  label: string;
  /**
   * `visible` answers for any column key, including the inline ones — a cell
   * that draws a second value inside itself asks before drawing it.
   */
  render: (row: T, lookup: Lookup, visible: (key: string) => boolean) => ReactNode;
  /**
   * Drawn inside another column's cell rather than in one of its own — an
   * email under a name, a posting under a club. It gets no header and no
   * column, but it does get a switch in Customize Columns, which is the whole
   * point: a value nested in someone else's cell was the one thing there was
   * no way to turn off.
   */
  inline?: boolean;
  /** A field from the API's sort allowlist; absent means the column is not sortable. */
  sortKey?: string;
  /** Plain value for CSV export; defaults to the row's field named by `key`. */
  csv?: (row: T) => string | number | null | undefined;
  className?: string;
  /** The API fields this column reads, when they are not just `key`. */
  fields?: string[];
}

export interface RowAction {
  label: string;
  run: () => void;
  danger?: boolean;
}

export interface ResourceConfig<T extends { id: number }> {
  title: string;
  /** One line under the title saying what this list is for. */
  description?: string;
  singular: string;
  endpoint: string;
  /** What a downloaded CSV is named, before the date. Defaults to the endpoint. */
  csvBase?: string;
  columns: ColumnDef<T>[];
  defaultSort?: { key: string; order: "asc" | "desc" };
  filters?: FilterDef[];
  fields: FieldDef[];
  toForm: (row: T) => Record<string, string>;
  /** Starting values for a new row — today's date, the viewer's club, and so on. */
  newDefaults?: (profile: Profile) => Record<string, string>;
  /** Extra controls before the refresh button. */
  toolbar?: ReactNode;
  /** A bespoke create/edit dialog, when the generic field form is not enough. */
  renderForm?: (editing: "new" | T, onClose: () => void, onSaved: () => void) => ReactNode;
  /** A bespoke detail dialog. */
  /**
   * A bespoke detail view. It takes the page over exactly as the generic one
   * does, and is handed the same actions, so a screen with its own body still
   * gets the same back link, Refresh, Edit and Delete.
   */
  renderView?: (row: T, ctx: DetailContext) => ReactNode;
  /** Extra entries in the row menu, between Edit and Delete. `refresh` reloads the list. */
  rowActions?: (row: T, refresh: () => void) => RowAction[];
  /** No add, edit, delete or selection — a record of what happened. */
  readOnly?: boolean;
  /**
   * The backend model name — `province`, `user` — that the screen's codenames
   * are built from. With it, Add, Edit and Delete show only to a person who
   * holds `add__`, `change__` and `delete__` on it. Without it nothing is hidden.
   */
  model?: string;
  /**
   * The list scrolls on rather than paging, and shows no total: the API
   * answers `{items, next}` and is asked for the rows below `next` when the
   * table reaches its end. For a log too large to count, let alone page.
   */
  infinite?: boolean;
  /** Rows can be added and deleted but never changed — a record of a moment. */
  noEdit?: boolean;
  /**
   * Rows are never deleted. Not the same as `readOnly`: they can still be added
   * and changed. There is no delete route behind these either — a member and a
   * walk-in are records of something that happened.
   */
  noDelete?: boolean;
  /** No detail view: no row menu and no click-through on the id. With readOnly, no actions at all. */
  noView?: boolean;
  /** The column whose cell opens the detail view; defaults to `id`. */
  viewColumn?: string;
  /** No row menu at all — the detail view carries the actions. */
  noRowMenu?: boolean;
  /** Recent searches under the box, and a Time Range that filters created_at. */
  searchHistory?: boolean;
  /**
   * Where recent searches live. Absent, the browser keeps them; given, the
   * server does, so they follow the account from one machine to the next.
   */
  searchHistoryStore?: {
    /**
     * The list, or only what has changed.
     *
     * `since` is the newest `updated_at` already held: the answer then carries
     * the handful of rows touched after it rather than the whole thousand.
     * Called without one when there is nothing held yet, and when a copy is
     * being reconciled — a delta can carry an edit but never an absence.
     */
    load: (since?: string, signal?: AbortSignal) => Promise<HistoryEntry[]>;
    /**
     * Files a committed term and answers with everything changed since `since`
     * — the term included — so keeping the held copy in step costs no request
     * of its own.
     */
    remember?: (term: string, since?: string) => Promise<HistoryEntry[]>;
    forget: (id: number) => Promise<unknown>;
  };
  /**
   * How much has to be typed before the search runs and is remembered. A short
   * prefix over a very large table is work with a useless answer; a small
   * catalogue leaves this at 0 and searches from the first letter.
   */
  searchMinLength?: number;
  /**
   * Typing suggests, Enter searches.
   *
   * The box matches the held history locally on every keystroke — instant, and
   * no request — while the list is left alone until a term is committed by
   * pressing Enter or picking a suggestion. For a table where a query per pause
   * in typing is real work, which is any table worth searching.
   */
  searchOnCommit?: boolean;
  /** Send the list's filters as a JSON body to `POST {endpoint}/query` rather than a query string. */
  listMethod?: "get" | "post";
  /**
   * Where the server stops counting. A list in the tens of millions is not
   * counted for every page; at or past this the footer says "10,000+".
   */
  countCap?: number;
  /** Text on the filter button instead of a bare icon. */
  filterLabel?: string;
  /** Beside the filter button: a control that reads or sets the filters — saved sets, say. */
  filterExtra?: (values: FilterValues, onApply: (values: FilterValues) => void) => ReactNode;
  /** A bespoke filters dialog. */
  renderFilters?: (
    values: FilterValues,
    onApply: (values: FilterValues) => void,
    onClose: () => void,
  ) => ReactNode;
  /** Customize Columns: show/hide, remembered per browser. */
  columnPicker?: boolean;
  /** Export the current page as CSV. */
  exportable?: boolean;
  /**
   * What the export button does when the screen does not hold every matching
   * row. It is handed the very parameters the list is asking with — so a
   * download is the same question, without the page — along with what is on
   * screen, what matches, and a way to write the screen out instead. Without
   * it the button exports the page and says nothing about the rest.
   */
  exportAll?: (choice: {
    params: ListParams;
    onScreen: number;
    total: number;
    /** The total as the pager writes it — "10,000+" when the count stopped at the cap. */
    totalLabel: string;
    /** The count stopped at the cap, so `total` is a floor and not a number of rows. */
    capped: boolean;
    /**
     * Every column, each with the API fields behind it and whether Customize
     * Columns has it on — the same list that panel shows, in the same order,
     * ticked the same way. So a download can drop a column it does not need,
     * or take one the table is not currently showing, without either screen
     * having to be visited first.
     */
    columns: { key: string; label: string; fields: string[]; selected: boolean }[];
    /** Write the rows already on screen, narrowed to these column keys. */
    writeScreen: (keys: string[]) => void;
  }) => void;
  /** Extra rows in the overflow menu — where a screen's rarer actions belong. */
  menuExtra?: ReactNode;
  /** Two-column form. */
  formColumns?: 1 | 2;
  /** Columns hidden until the person turns them on. */
  hiddenByDefault?: string[];
  /** Ask the API for only the visible columns' fields; rows are fetched in full to view or edit. */
  projectFields?: boolean;
  /** Rendered under the field list in the detail dialog — a row's child records. */
  renderDetailExtra?: (row: T) => ReactNode;
  /**
   * Per-row overrides for Edit and Delete, where the rule is about the record
   * rather than the screen — a superuser account nobody else may touch. The
   * API refuses either way; this stops offering a button that cannot work.
   */
  rowCanEdit?: (row: T, profile: Profile | undefined) => boolean;
  rowCanDelete?: (row: T, profile: Profile | undefined) => boolean;
  /** Fields the detail view leaves out because `renderDetailExtra` shows them better. */
  detailHide?: string[];
  /**
   * Headings for the detail view, in order. A section either names the fields
   * under it or draws its own content — a picture, a club and its posting —
   * so the things that are not one value per row still sit where they belong
   * in the reading order rather than in a slab underneath everything.
   *
   * Anything unnamed falls into a last untitled group, and the created/updated
   * footnote stays after all of them: a record's own history reads last.
   */
  detailSections?: { title: string; fields?: string[]; render?: (row: T) => ReactNode }[];
  /** What a field is called in the detail view; anything unnamed is titled from its key. */
  detailLabels?: Record<string, string>;
  /** Replaces the Add button — a menu, a link, anything that starts a create. */
  addMenu?: ReactNode;
  /** Text on the add button; defaults to "Add {title}". */
  addLabel?: string;
  /** How the count line names many of them; defaults to "{singular}s". */
  plural?: string;
  /** A back arrow before the title. */
  backTo?: "/employees" | "/members" | "/yellow-page" | "/";
  searchPlaceholder?: string;
}

export const fmtDate = (value: string) => format(new Date(value), "MM/dd/yyyy hh:mm:ss a");

/** A stored search term is lower-cased; the dropdown shows it the way a name reads. */
const titleCase = (value: string) => value.replace(/\b\p{L}/gu, (c) => c.toUpperCase());

/** An identifier — employee, account, card number — set the way it is printed on the card. */
export function Mono({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <span className={cn("text-subhead font-mono tracking-[0.01em] tabular-nums", className)}>
      {children}
    </span>
  );
}

const SIZES = [10, 25, 50, 100];
/** Columns whose ids are accounts, resolved through the accounts list. */
const ACCOUNT_LOOKUPS = new Set(["created_by_id", "updated_by_id", "manager_id"]);

/** One remembered search, as the store holds it. */
export interface HistoryEntry {
  id?: number;
  term: string;
  updated_at?: string;
}

/** How many suggestions the box shows. The rest of the copy is what makes a
 *  prefix match find something worth showing. */
const SUGGESTIONS_SHOWN = 10;

/**
 * How long the held search history may go without a full read.
 *
 * Deletions are the only thing a delta cannot carry, and they are rare and not
 * urgent: a term somebody removed on another device staying in this browser's
 * suggestions for half an hour is a smaller cost than every refetch in that
 * half hour re-reading a thousand rows to notice it.
 */
const RECONCILE_EVERY_MS = 30 * 60_000;

/**
 * When each screen's history was last read whole, kept outside the component
 * so leaving the screen and coming back is a delta like any other refetch.
 */
const lastWholeRead = new Map<string, number>();

/** The Time Range as an ISO instant, for narrowing the held copy locally. */
const windowStart = (window: string): string | undefined => {
  const spans: Record<string, number> = {
    hour: 3_600_000,
    day: 86_400_000,
    week: 7 * 86_400_000,
    month: 28 * 86_400_000,
  };
  const span = spans[window];
  return span ? new Date(Date.now() - span).toISOString() : undefined;
};

/** What every `/compact` route caps `limit` at. Asking for more is a 422. */
const COMPACT_MAX = 200;

const ACCOUNTS = "/users/compact";

/** One shared empty map, so a miss does not make a new object every render. */
const EMPTY: Record<string, string> = {};

/**
 * What a filter holds: one value, or several where the endpoint takes a list.
 * The transport already carries both — `ListParams` and the body it becomes —
 * so a multi-select is a wider state here, not a wider protocol.
 */
export type FilterValues = Record<string, string | string[]>;

/** Whether a filter is actually narrowing anything. An empty list is not. */
export const isSet = (value: string | string[] | undefined): boolean =>
  Array.isArray(value) ? value.length > 0 : (value ?? "") !== "";

function readJson<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function writeJson(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage can be full or blocked; the page still works without it.
  }
}

/**
 * How far into a list a single jump may land, in rows. Past this the "last
 * page" affordances are not offered — see `canJumpToEnd`. Ten thousand rows is
 * an offset PostgreSQL walks without anyone noticing; eighty million is not.
 */
const DEEP_PAGE_ROWS = 10_000;

/**
 * What a downloaded list is called: `member_2026-09-06.csv`.
 *
 * One helper because the same list downloads two ways — the server streams the
 * whole filtered set, the browser writes the page it already has — and two
 * files of the same rows under two different names is a support question.
 * The base is not translated: a filename is not read by the person's locale.
 */
export const csvName = (base: string): string =>
  `${base}_${new Date().toISOString().slice(0, 10)}.csv`;

/** The visible columns of the current page as a CSV file, built in the browser. */
function exportCsv<T extends { id: number }>(base: string, cols: ColumnDef<T>[], rows: T[]) {
  const cell = (column: ColumnDef<T>, row: T) => {
    const raw = column.csv ? column.csv(row) : (row as Record<string, unknown>)[column.key];
    return `"${String(raw ?? "").replace(/"/g, '""')}"`;
  };
  const csv = [cols.map((c) => `"${c.label}"`).join(",")]
    .concat(rows.map((row) => cols.map((c) => cell(c, row)).join(",")))
    .join("\n");
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  const link = Object.assign(document.createElement("a"), {
    href: url,
    download: csvName(base),
  });
  document.body.append(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export function ResourcePage<T extends { id: number }>({
  config,
}: {
  config: ResourceConfig<T>;
}) {
  const {
    title,
    description,
    singular,
    endpoint,
    csvBase = endpoint.replace(/^\//, ""),
    countCap,
    columns: declared,
    defaultSort,
    filters = [],
    fields,
    toForm,
    newDefaults,
    toolbar,
    renderForm,
    renderView,
    rowActions,
    readOnly = false,
    model,
    noEdit: noEditDeclared = false,
    noDelete: noDeleteDeclared = false,
    noView = false,
    viewColumn = "id",
    noRowMenu = false,
    backTo,
    searchPlaceholder,
    searchHistory = false,
    searchHistoryStore,
    searchMinLength = 0,
    searchOnCommit = false,
    listMethod = "get",
    filterLabel,
    renderFilters,
    filterExtra,
    // Every list can choose its columns; the choice is remembered per browser
    // under `columns:<endpoint>`, so one screen's layout is not another's.
    columnPicker = true,
    projectFields = false,
    renderDetailExtra,
    detailHide,
    detailSections,
    detailLabels,
    rowCanEdit,
    rowCanDelete,
    addMenu,
    exportable = false,
    exportAll,
    menuExtra,
    formColumns = 1,
    // The audit columns are available everywhere and off everywhere until
    // someone asks for them; a screen can still name others.
    hiddenByDefault = [],
    addLabel,
    plural = `${singular}s`,
    infinite = false,
  } = config;
  const t = useTranslation();
  const queryClient = useQueryClient();
  const { data: profile } = useQuery(profileQuery());
  // What the person may do to this model; a screen with no model hides nothing.
  const canAdd = !model || can(profile, `add__${model}`);
  const noEdit = noEditDeclared || (!!model && !can(profile, `change__${model}`));
  const noDelete = noDeleteDeclared || (!!model && !can(profile, `delete__${model}`));

  const [search, setSearch] = useState("");
  // What the list is filtered by, which is not the same as what is in the box.
  const [deferredSearch, setDeferredSearch] = useState("");
  const longEnough = (value: string) => value.trim().length >= searchMinLength;
  // With `searchOnCommit` the list waits for `commit`. Without it, it follows
  // the typing after a pause — and below `searchMinLength` it asks nothing.
  useEffect(() => {
    if (searchOnCommit) return;
    const id = setTimeout(() => setDeferredSearch(longEnough(search) ? search : ""), 400);
    return () => clearTimeout(id);
  }, [search, searchOnCommit]);
  // The suggestions do follow every keystroke, after a short pause of their own.
  const [typing, setTyping] = useState("");
  useEffect(() => {
    const id = setTimeout(() => setTyping(search.trim()), 150);
    return () => clearTimeout(id);
  }, [search]);
  const [sort, setSort] = useState(defaultSort?.key ?? "name");
  const [order, setOrder] = useState<"asc" | "desc">(defaultSort?.order ?? "asc");
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(25);
  const [filterValues, setFilterValues] = useState<FilterValues>({});
  const [showFilters, setShowFilters] = useState(false);
  const [editing, setEditing] = useState<"new" | T | null>(null);
  /**
   * The declared columns plus the two the API already sends on every read.
   *
   * "Who changed this last, and when" is a question of every list, and every
   * read schema answers it — it was simply never offered. Appended rather than
   * written into nine screens, off unless someone turns them on, and skipped
   * where a screen defines its own.
   */
  const columns = useMemo(() => {
    const audit: ColumnDef<T>[] = [
      {
        key: "updated_at",
        label: t("rp.updatedAt"),
        sortKey: "updated_at",
        render: (row) => fmtDate((row as Record<string, unknown>)["updated_at"] as string),
      },
      {
        key: "updated_by_id",
        label: t("rp.updatedBy"),
        render: (row, lookup) =>
          lookup("created_by_id", (row as Record<string, unknown>)["updated_by_id"] as number),
      },
    ];
    const named = new Set(declared.map((column) => column.key));
    // Nothing changes these rows — a log, or a record of a moment — so there is
    // no updated_at and no updated_by_id to show. Offering them put an "Updated
    // By" column of `undefined` in the table and two dead entries in Customize
    // Columns, beside a date the row does not have either.
    if (readOnly || noEdit) return declared;
    return [...declared, ...audit.filter((column) => !named.has(column.key))];
  }, [declared, t, readOnly, noEdit]);

  const [viewing, setViewing] = useState<T | null>(null);
  // A record is always read when it is opened, so there is a wait to show.
  const [opening, setOpening] = useState(false);
  const [refreshingOne, setRefreshingOne] = useState(false);
  /**
   * Which record is open, and which is being edited, live in the address bar.
   *
   * They were remembered for the tab, which could not tell a reload from
   * arriving fresh: opening province 4, going to Cities and coming back to
   * Provinces reopened province 4 — a page that would not let go. The URL has
   * no such trouble. It also gives the panel a Back that closes it and a link
   * that opens it.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const urlSearch = Object.fromEntries(searchParams.entries()) as Record<string, unknown>;
  const viewToken = typeof urlSearch["view"] === "string" ? urlSearch["view"] : undefined;
  const editToken = typeof urlSearch["edit"] === "string" ? urlSearch["edit"] : undefined;

  const setParam = (key: "view" | "edit", id: number | null) =>
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        if (id === null) next.delete(key);
        else next.set(key, encodeRecordId(id));
        return next;
      },
      { replace: true },
    );

  // Only the address bar changes; the effect below reads the record. Seeding
  // the panel from the row here meant it opened on whatever the list last
  // fetched — stale, and on a screen that projects columns, missing fields.
  const open = (row: T | null) => setParam("view", row?.id ?? null);

  // The address bar is the truth: follow it, rather than only reading it once.
  // A token for a record that is gone, or one somebody typed, simply clears.
  useEffect(() => {
    const id = decodeRecordId(viewToken);
    if (id === null) {
      setViewing(null);
      return;
    }
    let cancelled = false;
    setOpening(true);
    void fetchOne(id)
      .then((row) => !cancelled && setViewing(row))
      .catch(() => !cancelled && setParam("view", null))
      .finally(() => !cancelled && setOpening(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewToken]);

  useEffect(() => {
    const id = decodeRecordId(editToken);
    if (id === null) {
      setEditing((current) => (current === "new" ? current : null));
      return;
    }
    let cancelled = false;
    setOpening(true);
    void fetchOne(id)
      .then((row) => !cancelled && setEditing(row))
      .catch(() => !cancelled && setParam("edit", null))
      .finally(() => !cancelled && setOpening(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editToken]);
  const [searchFocus, setSearchFocus] = useState(false);
  const [pickingColumns, setPickingColumns] = useState(false);
  // A delete is not undoable, so it is confirmed; nothing else here is.
  const [deleting, setDeleting] = useState<number | null>(null);

  // Remembered per browser: recent searches, and which columns are on.
  const historyKey = `search-history:${endpoint}`;
  const columnsKey = `columns:${endpoint}`;
  const [localHistory, setLocalHistory] = useState<string[]>(
    () => readJson<string[]>(historyKey) ?? [],
  );
  // The Time Range beside the history narrows the history, not the list.
  const [historyWindow, setHistoryWindow] = useState("");
  // One fetch of the whole list per page open, held by react-query for the rest
  // of the session and refetched when the window regains focus. That refetch is
  // also what reconciles a term deleted on another device: a "what changed
  // since" answer can carry an edit but never an absence.
  const historyKeyQ = [endpoint, "search-history"];
  // Set when the next read has to be the whole list rather than a delta —
  // after a delete, which is the one change this screen makes that a delta
  // cannot describe.
  const forceWhole = useRef(false);
  const storedHistory = useQuery({
    queryKey: historyKeyQ,
    queryFn: async ({ signal }) => {
      const held = queryClient.getQueryData<HistoryEntry[]>(historyKeyQ) ?? [];
      // A delta once there is something to add it to. A thousand terms is
      // about ten kilobytes gzipped, which is worth fetching once and not
      // again every time this screen is opened.
      //
      // The whole list is read when there is nothing to add to, after a
      // delete, and otherwise no more often than `RECONCILE_EVERY_MS` — a
      // delta carries what changed, and a row that is gone changed by not
      // being there. That interval is the longest a term deleted on another
      // device can linger in these suggestions, traded against every refetch
      // in between costing a handful of rows instead of a thousand.
      //
      // Asked here rather than on a focus listener so it covers every reason a
      // read happens: the tab coming back, the five-minute staleness, an
      // invalidate, and the screen being opened again. The watermark is worked
      // out rather than read off the first row — the order the server returns
      // is not this screen's to depend on.
      const aged = Date.now() - (lastWholeRead.get(endpoint) ?? 0) >= RECONCILE_EVERY_MS;
      const whole = forceWhole.current || held.length === 0 || aged;
      const since = whole ? "" : watermark(held);
      if (whole) lastWholeRead.set(endpoint, Date.now());
      forceWhole.current = false;
      const answer = await searchHistoryStore!.load(since || undefined, signal);
      return since ? folded(held, answer) : answer;
    },
    enabled: Boolean(searchHistoryStore) && searchHistory,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: true,
  });
  const history: HistoryEntry[] = searchHistoryStore
    ? (storedHistory.data ?? [])
    : localHistory.map((term) => ({ term }));
  const historyChanged = () => queryClient.invalidateQueries({ queryKey: historyKeyQ });
  /** The newest `updated_at` held, which is what a delta is asked for. */
  const watermark = (held: HistoryEntry[]): string =>
    held.reduce(
      (newest, entry) =>
        entry.updated_at && entry.updated_at > newest ? entry.updated_at : newest,
      "",
    );
  /** Fold a "what changed" answer into a copy: newest wins, by id. */
  const folded = (held: HistoryEntry[], delta: HistoryEntry[]): HistoryEntry[] => {
    const byId = new Map(held.map((entry) => [entry.id ?? entry.term, entry]));
    for (const entry of delta) byId.set(entry.id ?? entry.term, entry);
    return [...byId.values()].sort((a, b) =>
      (b.updated_at ?? "").localeCompare(a.updated_at ?? ""),
    );
  };
  const mergeHistory = (delta: HistoryEntry[]) => {
    if (delta.length === 0) return;
    queryClient.setQueryData<HistoryEntry[]>(historyKeyQ, (held = []) => folded(held, delta));
  };
  // What the box actually offers: the copy we hold, narrowed by the Time Range
  // and by what is being typed. No request — that is the whole point of holding
  // it. Matching is on the stored form, which is stripped and lower-cased.
  const suggestions = (() => {
    const from = windowStart(historyWindow);
    const typed = typing.toLowerCase();
    return history.filter(
      (entry) =>
        (!typed || entry.term.startsWith(typed)) &&
        (!from || !entry.updated_at || entry.updated_at >= from),
    );
  })();
  // How many suggestions the dropdown is showing. The whole history is already
  // in the browser — a thousand terms, fetched once — so "load more" is a
  // number going up rather than a request going out. Scrolling the dropdown
  // raises it; nothing is fetched, ever.
  const suggestionBox = useRef<HTMLDivElement>(null);
  const [suggestionsShown, setSuggestionsShown] = useState(SUGGESTIONS_SHOWN);
  // Reset when the list being scrolled changes, adjusted during render rather
  // than in an effect: an effect would paint the old count once and correct it.
  const suggestionScope = `${typing}|${historyWindow}`;
  const [scrolledScope, setScrolledScope] = useState(suggestionScope);
  if (suggestionScope !== scrolledScope) {
    setScrolledScope(suggestionScope);
    setSuggestionsShown(SUGGESTIONS_SHOWN);
  }
  useLoadMore(suggestionBox, suggestionsShown < suggestions.length, false, () =>
    setSuggestionsShown((shown) => shown + SUGGESTIONS_SHOWN),
  );

  const [localHidden, setLocalHidden] = useState<string[]>(
    () => readJson<string[]>(columnsKey) ?? [...hiddenByDefault, "updated_at", "updated_by_id"],
  );
  // The chosen columns are the browser's, and nothing else's. The list asks
  // for every column whatever is on screen, so showing one is a re-render and
  // not a request — which is what it looked like when the choice was a
  // projection the server had to answer first, and the two slowest requests on
  // the screen ran one after the other.
  const hidden = localHidden;
  const shown = (key: string) => !hidden.includes(key);
  const visibleColumns = columns.filter((c) => shown(c.key) && !c.inline);
  // What the *export* asks for, and nothing else. The list itself asks for
  // every column whatever is on screen — that is what makes showing one a
  // re-render rather than a request — but a download is a separate request
  // made once, deliberately, and it holds the columns the screen holds. Left
  // unprojected the file came back with all sixty-two, `Active Club Id` beside
  // `Active Club` and `Created By Id` beside `Created By`.
  // An inline column's fields are asked for too, since the cell it is drawn
  // inside needs them.
  // What the export dialog offers: every column, carrying the fields behind it,
  // so dropping one from the file drops its fields from the request rather than
  // trimming the answer afterwards. A–Z by label and not in table order, for
  // the reason `ColumnPicker` is: this list is read to *find* a column, and
  // table order is no help with that once there are thirty of them.
  const exportColumns = [...columns]
    .sort((a, b) => a.label.localeCompare(b.label))
    .map((c) => ({
      key: c.key,
      label: c.label,
      fields: c.fields ?? [c.key],
      selected: shown(c.key),
    }));
  const remember = (term: string) => {
    const value = term.trim();
    // Too short to have been searched, so too short to be worth keeping.
    if (!value || !longEnough(value)) return;
    if (searchHistoryStore) {
      const since = watermark(history);
      void searchHistoryStore.remember?.(value, since || undefined).then(mergeHistory);
      return;
    }
    const next = [value, ...localHistory.filter((h) => h !== value)].slice(0, 10);
    setLocalHistory(next);
    writeJson(historyKey, next);
  };
  /** Run the search that is in the box, and file it. What Enter does. */
  const commit = (term: string) => {
    const value = term.trim();
    setDeferredSearch(longEnough(value) ? value : "");
    setPage(1);
    remember(value);
  };
  const forget = (entry: { id?: number; term: string }) => {
    if (searchHistoryStore && entry.id !== undefined) {
      // The refetch this triggers has to be the whole list. A delta carries
      // what changed, and a row that is gone changed by not being there — ask
      // for what is new and the term just deleted comes straight back.
      forceWhole.current = true;
      void searchHistoryStore.forget(entry.id).then(historyChanged);
      return;
    }
    const next = localHistory.filter((h) => h !== entry.term);
    setLocalHistory(next);
    writeJson(historyKey, next);
  };

  // A scrolling list has no page and no sort: it is newest first, on and on.
  const params: ListParams = {
    search: deferredSearch,
    sort: infinite ? undefined : sort,
    order: infinite ? undefined : order,
    page: infinite ? undefined : page,
    size,
    ...filterValues,
  };
  const query = useQuery({
    queryKey: [endpoint, params],
    // The signal is what makes typing cheap: react-query aborts the request for
    // a term nobody is waiting for any more, so a search two keystrokes stale
    // stops costing the server anything the moment it is superseded.
    queryFn: ({ signal }) =>
      listMethod === "post"
        ? queryResource<T>(endpoint, params, signal)
        : listResource<T>(endpoint, params, signal),
    placeholderData: keepPreviousData,
    enabled: !infinite,
  });
  const feed = useInfiniteQuery({
    queryKey: [endpoint, params, "feed"],
    queryFn: ({ pageParam, signal }) =>
      listCursor<T>(endpoint, { ...params, after: pageParam }, signal),
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (last) => last.next ?? undefined,
    placeholderData: keepPreviousData,
    enabled: infinite,
  });
  // Whichever is live answers for loading, error and refresh.
  const listing = infinite ? feed : query;
  const scroller = useRef<HTMLDivElement>(null);
  // `isFetching`, not `isFetchingNextPage`: a refresh refetches every page
  // already loaded, so scrolling to the end while one is in flight would ask
  // for a page that answer is about to bring anyway.
  useLoadMore(
    scroller,
    infinite && !!feed.hasNextPage,
    feed.isFetching,
    () => void feed.fetchNextPage(),
  );
  /** A projected row is missing fields; viewing or editing needs the whole record. */
  const whole = async (row: T): Promise<T> =>
    projectFields ? (await api.get<T>(`${endpoint}/${row.id}`)).data : row;
  /** The record on its own, straight from the API — for opening and reopening one. */
  const fetchOne = async (id: number): Promise<T> =>
    (await api.get<T>(`${endpoint}/${id}`)).data;
  const invalidate = () => queryClient.invalidateQueries({ queryKey: [endpoint] });

  // Two lists, because they cost different things.
  //
  // A `select` is a small static set — or one loader call — and a column can be
  // rendering it right now: the activity log prints a reason and a status
  // through this, and holding it back showed people `account_locked`. So it
  // always loads. A `fk` is a round trip per filter to a /compact endpoint, and
  // only matters once a chip or an open record actually shows an id, so it
  // waits for one.
  const selectFilters = filters.filter(
    (filter) => filter.type === "select" && !filter.dependsOn,
  );
  const fkFilters = filters.filter(
    (filter) => filter.type === "fk" && filter.endpoint && !filter.dependsOn,
  );
  const selectLookups = useQuery({
    queryKey: ["lookups", "select", endpoint, selectFilters.map((filter) => filter.name)],
    queryFn: async ({ signal }) =>
      Object.fromEntries(
        await Promise.all(
          selectFilters.map(async (filter) => [
            filter.name,
            new Map(
              (
                await (filter.loadOptions?.("", signal) ??
                  Promise.resolve(filter.options ?? []))
              ).map((option) => [String(option.value), option.label]),
            ),
          ]),
        ),
      ) as Record<string, Map<string, string>>,
    enabled: selectFilters.length > 0,
    staleTime: 5 * 60_000,
  });
  // `keepPreviousData` keeps the last answer on screen while the next one
  // loads, which is what stops the table flashing empty on every page switch.
  // It keeps it when the next one *fails* too — and then the screen is the
  // previous page's rows under this page's number, beside a total nothing on
  // it adds up to, with the failure invisible below the fold. A table quietly
  // answering a question nobody asked is worse than one saying it could not
  // answer, so a failed fetch has no rows, no total and no pager: the error
  // fills the table and the chrome around it stays exactly as it is.
  const failed = listing.isError;
  const rows = failed
    ? []
    : infinite
      ? (feed.data?.pages.flatMap((p) => p.items) ?? [])
      : (query.data?.items ?? []);
  // Name the wait after whatever the person just did. "Loading" on a screen
  // where somebody pressed Search says less than "Searching members".
  const waitingFor: LoadingAction = deferredSearch
    ? "searching"
    : Object.values(filterValues).some((value) => value !== "")
      ? "filtering"
      : "loading";
  // The skeleton stands in for content that is not there yet, so it goes up at
  // once — there is nothing for it to flash over. The note is the opposite: on
  // a fast answer it would be a flicker, so it waits until the wait is real.
  const showSkeleton = listing.isPending;
  const slow = useDelayed(listing.isFetching);
  // While the list is working, everything that would start a second piece of
  // work is off: filtering, choosing columns, exporting rows that are about to
  // change, adding a record. Refresh stays — it is the one control whose job is
  // this — and so does the search box, which locks itself between Enter and its
  // answer.
  const busy = listing.isFetching;

  // Names for the ids on screen, and nothing else.
  //
  // A /compact response carries the id and the name together, so a label we
  // have been told once is never asked for again — scrolling the Club dropdown
  // names every club a chip, a column or a detail row can go on to show. What
  // is left is fetched by id, in one request per endpoint. The old shape was a
  // blanket page per endpoint, which is both more traffic and wrong past the
  // 200-row cap: the author of row 300 was in nobody's first page and rendered
  // as "300".
  const endpoints = [...new Set([...fkFilters.map((filter) => filter.endpoint!), ACCOUNTS])];
  const stores = useQueries({
    queries: endpoints.map((each) => ({
      queryKey: labelsKey(each),
      // Never fetches; `fetchCompact` fills it, whoever calls it and wherever.
      enabled: false,
      initialData: (): Record<string, string> => ({}),
      staleTime: Infinity,
    })),
  });
  const seen = (each: string): Record<string, string> =>
    (stores[endpoints.indexOf(each)]?.data as Record<string, string> | undefined) ?? EMPTY;
  /** Which endpoint names this id column, if any. */
  const endpointFor = (name: string): string | undefined =>
    ACCOUNT_LOOKUPS.has(name)
      ? ACCOUNTS
      : fkFilters.find((filter) => filter.name === name)?.endpoint;

  const onScreen = [...rows, viewing];
  const wanted = endpoints.map((each) => {
    const names = [...ACCOUNT_LOOKUPS, ...fkFilters.map((filter) => filter.name)].filter(
      (name) => endpointFor(name) === each,
    );
    const ids = new Set<number>();
    for (const name of names) {
      const picked = filterValues[name];
      const chosen = Number(Array.isArray(picked) ? (picked[0] ?? "") : (picked ?? ""));
      if (chosen > 0) ids.add(chosen);
      for (const row of onScreen) {
        const value = row ? (row as Record<string, unknown>)[name] : null;
        if (typeof value === "number" && value > 0) ids.add(value);
      }
    }
    const known = seen(each);
    return [...ids]
      .filter((id) => !(String(id) in known))
      .sort((a, b) => a - b)
      .slice(0, COMPACT_MAX);
  });
  // Nothing reads the results: `fetchCompact` files each response under its
  // endpoint, and `stores` is what re-renders when one lands.
  useQueries({
    queries: endpoints.map((each, index) => ({
      queryKey: ["compact", each, "ids", wanted[index]],
      queryFn: ({ signal }) => fetchCompact(each, { id: wanted[index] }, signal),
      enabled: (wanted[index]?.length ?? 0) > 0,
      staleTime: 5 * 60_000,
    })),
  });

  const lookup: Lookup = (filter, value) => {
    if (value === null) return "—";
    const key = String(value);
    // A yes/no filter stores the string the query carries, and every dropdown
    // that sets one offers Yes and No. A chip reporting it as `true` is the
    // same filter answering in a different language from the control that set
    // it — so it answers here in the one the person read.
    if (filters.find((each) => each.name === filter)?.type === "boolean") {
      if (key === "true") return t("rp.yes");
      if (key === "false") return t("rp.no");
    }
    const from = endpointFor(filter);
    return (
      selectLookups.data?.[filter]?.get(key) ?? (from ? seen(from)[key] : undefined) ?? key
    );
  };

  const activeFilters = Object.values(filterValues).filter(isSet).length;
  const applyFilters = (next: FilterValues) => {
    setFilterValues(next);
    setPage(1);
    setShowFilters(false);
  };
  const clearFilters = () => {
    setFilterValues({});
    setPage(1);
  };
  const sortBy = (key: string) => {
    setPage(1);
    if (key === sort) setOrder((current) => (current === "asc" ? "desc" : "asc"));
    else {
      setSort(key);
      setOrder("asc");
    }
  };

  /** The stamp of the row about to be deleted, from the list or the open detail. */
  const stampOf = (id: number) => {
    const row = rows.find((r) => r.id === id) ?? (viewing?.id === id ? viewing : undefined);
    return (row as { updated_at?: string } | undefined)?.updated_at;
  };
  const remove = useMutation({
    mutationFn: (id: number) => deleteResource(endpoint, id, stampOf(id)),
    onSuccess: () => {
      toast.success(t("rp.deleted").replace("{singular}", singular));
      void invalidate();
    },
    onError: (error) =>
      toast.error(
        errorMessage(error, t("rp.deleteFailed").replace("{singular}", singular.toLowerCase())),
      ),
  });

  const actions = !noRowMenu && !(readOnly && noView);
  const total = failed ? 0 : (query.data?.total ?? 0);
  // The server stops counting at `countCap` and reports a floor, because an
  // exact total over tens of millions means reading every matching row — 20s
  // on the filters people actually use. So the number is shown as "10,000+"
  // once it reaches the cap: the same answer, and the same next move, without
  // the wait. A list with no cap set is counted exactly and reads exactly.
  //
  // `total - 1` because a capped total is a floor built as "what has been read,
  // and one more": the row past the cap is what proves there is more, and is
  // not itself a count. So ten thousand and one reads "10,000+", and page three
  // hundred of the same list reads "15,050+" rather than falling back to the cap.
  const capped = countCap !== undefined && total > countCap;
  const totalLabel = capped ? `${(total - 1).toLocaleString()}+` : total.toLocaleString();
  const pages = failed ? 1 : Math.max(1, query.data?.pages ?? 1);
  // Paging is `LIMIT … OFFSET`, and an offset is rows the database walks and
  // throws away before it returns anything. On a list of forty million that
  // makes "last page" `OFFSET 79,999,950` — a table scan for fifty rows, and
  // on a partitioned table, one per partition.
  //
  // So past this the end of the list is not offered at all: neither the `»`
  // button nor the `… 1,600,000` beside it. Not disabled — gone. A control
  // that is greyed out for a reason nothing on screen explains reads as
  // broken, and the row count above the pager already says how long the list
  // is, which is what the page number was standing in for. Short lists keep
  // both, which is where the jump was ever any use: on thirty clubs the last
  // page is the second.
  const canJumpToEnd = (pages - 1) * size <= DEEP_PAGE_ROWS;

  const confirmDelete = (id: number) => setDeleting(id);

  /**
   * Asked wherever Delete was pressed.
   *
   * The detail view used to close itself and hand the question to the list,
   * so answering "no" left you somewhere you had not asked to be. It stays put
   * now; only an actual delete closes it, because the record is gone.
   */
  const confirmDialog = (
    <ConfirmDialog
      open={deleting !== null}
      onClose={() => setDeleting(null)}
      onConfirm={() => {
        if (deleting === null) return;
        remove.mutate(deleting, {
          onSuccess: () => open(null),
          onSettled: () => setDeleting(null),
        });
      }}
      loading={remove.isPending}
      title={t("rp.confirmDeleteTitle").replace("{singular}", singular)}
      message={t("rp.confirmDelete").replace("{singular}", singular.toLowerCase())}
      confirmLabel={t("common.delete")}
    />
  );

  /** Every filter that is currently narrowing the list, named and removable. */
  const chips = Object.entries(filterValues)
    .filter(([, value]) => isSet(value))
    .map(([name, value]) => ({
      name,
      label: filters.find((filter) => filter.name === name)?.label ?? humanize(name),
      // Several values read as one chip: "Gold, Business", which is what the
      // filter says, rather than a chip each for a single condition.
      value: (Array.isArray(value) ? value : [value])
        .map((one) => lookup(name, one))
        .join(", "),
    }));

  // A bespoke form takes the page over exactly as the generic one does — it is
  // still a create or an edit, and it clears the `edit` parameter on the way
  // out the same way. Rendered after the list instead, it opened underneath a
  // table that was still on screen.
  //
  // A record is read when it is opened, so between the click and the answer
  // there is nothing to draw a panel from. The list would otherwise sit there
  // looking as though the click had missed.
  if (opening) {
    return (
      <Page>
        <PageHeader
          back={
            <button
              type="button"
              onClick={() => {
                setParam("view", null);
                setParam("edit", null);
              }}
              className={backLinkClass}
            >
              <BackArrow label={singular} />
            </button>
          }
          eyebrow={singular}
          title={<Skeleton className="h-7 w-56" />}
        />
        <LoadingNote subject={singular.toLowerCase()} className="mb-3" />
        <section className="bg-surface-raised rounded-card border-line grid gap-4 border px-6 py-5">
          {Array.from({ length: 6 }, (_, index) => (
            <div key={index} className="grid grid-cols-[minmax(0,10rem)_minmax(0,1fr)] gap-x-3">
              <Skeleton className="h-4 w-24" />
              <Skeleton className={index % 3 === 0 ? "h-4 w-1/2" : "h-4 w-2/3"} />
            </div>
          ))}
        </section>
      </Page>
    );
  }

  if (editing) {
    // Opened from a record, `view` is still in the address bar: clearing `edit`
    // alone lands back on that record, and the row is refetched so it shows
    // what was just saved.
    const close = () => {
      setEditing(null);
      setParam("edit", null);
    };
    const saved = () => {
      close();
      if (editing !== "new" && viewing) void fetchOne(editing.id).then(setViewing);
      void invalidate();
    };
    if (renderForm) return <Page>{renderForm(editing, close, saved)}</Page>;
    return (
      <Page>
        <EditDialog
          variant="panel"
          singular={singular}
          endpoint={endpoint}
          fields={fields}
          columns={formColumns}
          initial={
            editing === "new"
              ? newDefaults && profile
                ? newDefaults(profile)
                : {}
              : toForm(editing)
          }
          id={editing === "new" ? null : editing.id}
          stamp={editing === "new" ? null : (editing as { updated_at?: string }).updated_at}
          onClose={close}
          onSaved={saved}
        />
      </Page>
    );
  }

  // Adding and editing take over the page, the way the configuration screens
  // and the role editor do: a form that replaces what it is a form for, rather
  // than a sheet over a table nobody can use while it is open.
  // Viewing takes over the page too, so a record is read where it was opened
  // rather than in a drawer over the table it came from — and can be acted on
  // there. Edit and Delete follow the same flags the row menu does.
  if (viewing) {
    const ctx: DetailContext = {
      onClose: () => open(null),
      lookup,
      busy: refreshingOne,
      onRefresh: () => {
        setRefreshingOne(true);
        void fetchOne(viewing.id)
          .then(setViewing)
          .finally(() => setRefreshingOne(false));
      },
      onEdit:
        readOnly || noEdit || rowCanEdit?.(viewing, profile) === false
          ? undefined
          : () => setParam("edit", viewing.id),
      onDelete:
        readOnly || noDelete || rowCanDelete?.(viewing, profile) === false
          ? undefined
          : () => confirmDelete(viewing.id),
    };
    if (renderView) {
      return (
        <Page>
          {renderView(viewing, ctx)}
          {confirmDialog}
        </Page>
      );
    }
    return (
      <Page>
        {/* The edit leaves `view` in the address bar, so closing or saving it
            comes back to this record rather than dropping to the list. */}
        <DetailPanel
          singular={singular}
          row={viewing}
          {...ctx}
          extra={renderDetailExtra?.(viewing)}
          hide={detailHide}
          sections={detailSections}
          labels={detailLabels}
          actions={rowActions?.(viewing, () => {
            void fetchOne(viewing.id).then(setViewing);
            void invalidate();
          })}
        />
        {confirmDialog}
      </Page>
    );
  }

  return (
    <Page fill>
      <PageHeader
        title={title}
        description={description}
        back={
          backTo ? (
            <Link to={backTo} className={backLinkClass}>
              <BackArrow label={t("rp.back")} />
            </Link>
          ) : undefined
        }
      />

      {/* Every line is packed to the right, and the search box holds the left
          edge with an auto margin of its own. One line: search left, actions
          right. Two: the search box and what fits beside it on the first, the
          remainder — Add among them, and last — against the right edge of the
          second. */}
      <Toolbar className="justify-end">
        <div className="relative w-full sm:mr-auto sm:w-80">
          <SearchInput
            value={search}
            onChange={(event) => {
              setPage(1);
              setSearch(event.target.value);
            }}
            onClear={() => {
              setPage(1);
              setSearch("");
              setDeferredSearch("");
            }}
            onFocus={() => setSearchFocus(true)}
            onBlur={() => setTimeout(() => setSearchFocus(false), 150)}
            // `readOnly` stops typing and nothing else: Enter still fires, and
            // would start a second read while the first is in the air.
            onKeyDown={(event) => event.key === "Enter" && !busy && commit(search)}
            // Held only where a search is committed rather than typed. On a
            // `searchOnCommit` screen the box locks between Enter and the
            // answer, which is a real wait a person chose to start. Where the
            // list follows the typing, `busy` is true for most of the typing —
            // disabling it there takes the keyboard away mid-word, which is the
            // members-search bug this session opened with.
            readOnly={searchOnCommit && busy}
            aria-busy={listing.isFetching}
            placeholder={searchPlaceholder ?? t("rp.search")}
            aria-label={`${t("rp.search")} ${title.toLowerCase()}`}
          />
          {searchHistory && searchFocus ? (
            <div
              className={cn(
                "border-line bg-surface-raised animate-sheet absolute top-full left-0 z-30 mt-1.5 grid max-w-[calc(100vw-2.5rem)] grid-cols-1 overflow-hidden rounded-xl border shadow-[var(--shadow-float)]",
                searchHistoryStore
                  ? "w-[38rem] sm:grid-cols-[minmax(0,1fr)_13rem]"
                  : "w-[24rem]",
              )}
            >
              <div className="p-2">
                <p className="text-fg-subtle text-footnote px-2 pt-1 pb-1.5 font-medium">
                  {typing ? t("rp.suggestions") : t("rp.searchHistory")}
                </p>
                {suggestions.length === 0 ? (
                  <p className="text-fg-subtle text-subhead px-2 py-3">
                    {t("common.nothingYet")}
                  </p>
                ) : (
                  <div ref={suggestionBox} className="max-h-80 overflow-y-auto">
                    {suggestions.slice(0, suggestionsShown).map((entry) => (
                      <div
                        key={entry.id ?? entry.term}
                        className="hover:bg-surface-inset flex items-center gap-1 rounded-lg pr-1"
                      >
                        <button
                          type="button"
                          disabled={busy}
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={() => {
                            // What the row shows is what the box gets; the match is case-blind.
                            setSearch(titleCase(entry.term));
                            commit(entry.term);
                          }}
                          className="text-fg text-callout flex min-w-0 flex-1 items-center gap-2.5 rounded-lg px-2.5 py-2 text-left"
                        >
                          <Search className="text-fg-subtle size-4 shrink-0" aria-hidden />
                          <span className="min-w-0 flex-1 truncate">
                            {titleCase(entry.term)}
                          </span>
                          {entry.updated_at ? (
                            <span className="text-fg-subtle text-footnote shrink-0 tabular-nums">
                              {fmtDate(entry.updated_at)}
                            </span>
                          ) : null}
                        </button>
                        <button
                          type="button"
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={() => forget(entry)}
                          className="text-fg-subtle hover:text-fg grid size-6 shrink-0 place-items-center rounded-full"
                        >
                          <X className="size-3.5" aria-hidden />
                          <span className="sr-only">
                            {t("rp.removeSearch").replace("{term}", entry.term)}
                          </span>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                {searchHistoryStore ? (
                  // Server-kept history is swept a month after a term was last
                  // used, so say so rather than let people wonder where an old
                  // search went. The browser-kept one has no such window.
                  <p className="text-fg-subtle text-footnote border-line mt-1.5 border-t px-2 pt-2">
                    {t("rp.searchHistoryKept")}
                  </p>
                ) : null}
              </div>
              {searchHistoryStore ? (
                <div className="bg-surface-sunken border-line p-2 sm:border-l">
                  <p className="text-fg-subtle text-footnote px-2 pt-1 pb-1.5 font-medium">
                    {t("rp.timeRange")}
                  </p>
                  {(
                    [
                      ["hour", t("rp.lastHour")],
                      ["day", t("rp.last24h")],
                      ["week", t("rp.last7d")],
                      ["month", t("rp.last4w")],
                      ["", t("rp.allTime")],
                    ] as const
                  ).map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => setHistoryWindow(value)}
                      className={cn(
                        "text-callout block w-full rounded-lg px-2.5 py-2 text-left transition-colors",
                        historyWindow === value
                          ? "text-accent bg-surface-raised font-medium"
                          : "text-fg hover:bg-surface-raised",
                      )}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

        {filters.length || renderFilters ? (
          <Button
            variant="secondary"
            disabled={busy}
            className={cn(activeFilters > 0 && "border-accent text-accent")}
            aria-expanded={showFilters}
            onClick={() => setShowFilters((open) => !open)}
          >
            <ListFilter aria-hidden />
            {filterLabel ?? t("rp.filters")}
            {activeFilters > 0 ? (
              <span className="bg-accent text-accent-fg text-footnote grid min-w-5 place-items-center rounded-full px-1.5 tabular-nums">
                {activeFilters}
              </span>
            ) : null}
          </Button>
        ) : null}
        {/* Saved filters and anything else a screen puts beside the filter
            button. Applying one is a read, so it waits like the rest. */}
        <fieldset
          disabled={busy}
          className="contents [&:disabled_a]:pointer-events-none [&:disabled_a]:opacity-50"
        >
          {filterExtra?.(filterValues, applyFilters)}
        </fieldset>

        {/* Every control is a sibling of the search box, not a block beside it.
            Nested, the actions wrapped together the moment the last one did not
            fit, so a row with room for three more buttons showed none of them.
            Loose, they fill the line and only the remainder goes below.

            The add control stays last in the source, so it is the rightmost
            thing on whichever line it lands on. */}
        {/* Each says what it is: a lone glyph beside worded buttons reads as
            decoration. */}
        {columnPicker ? (
          <Button variant="secondary" disabled={busy} onClick={() => setPickingColumns(true)}>
            <Columns3 aria-hidden />
            {t("rp.columns")}
          </Button>
        ) : null}
        <Button
          variant="secondary"
          disabled={busy}
          aria-busy={listing.isFetching || undefined}
          onClick={() => void listing.refetch()}
        >
          <RotateCw className={cn(listing.isFetching && "animate-spin")} aria-hidden />
          {t("rp.refresh")}
        </Button>
        {/* The slots a screen fills itself — Members puts Saved filters, Batch,
            an Activity Log link and its own Add menu here. A fieldset holds
            them without each screen having to thread `busy` into every button
            it supplies; `contents`, so they keep their own places in the row.
            Not the search box above, which is how a search is corrected. */}
        <fieldset
          disabled={busy}
          // A fieldset disables form controls and stops at anchors, and a
          // screen's slots hold both — Members puts an Activity Log link in
          // one. The rule reaches those too, so a caller does not have to know
          // which of its buttons is really a link.
          className="contents [&:disabled_a]:pointer-events-none [&:disabled_a]:opacity-50"
        >
          {toolbar}
          {/* Every action is a button that says what it is. Behind a ⋮ they were
              two clicks and a guess, on a row that had the room. */}
          {exportable ? (
            <Button
              variant="secondary"
              onClick={() => {
                // The screen already holds every match when the total is no
                // larger than what is on it — nothing to ask about and nothing
                // to fetch, so it is written from here.
                if (!exportAll || rows.length >= total) {
                  exportCsv(csvBase, visibleColumns, rows);
                  return;
                }
                exportAll({
                  params,
                  onScreen: rows.length,
                  total,
                  totalLabel,
                  capped,
                  columns: exportColumns,
                  // Filtered from every column, not the visible ones: the list
                  // asks for all of them whatever is on screen, so a column the
                  // table is hiding is already in the rows the browser holds.
                  writeScreen: (keys) =>
                    exportCsv(
                      csvBase,
                      columns.filter((c) => keys.includes(c.key)),
                      rows,
                    ),
                });
              }}
            >
              <Download aria-hidden />
              {t("rp.export")}
            </Button>
          ) : null}
          {menuExtra}
          {readOnly || !canAdd
            ? null
            : (addMenu ?? (
                <Button onClick={() => setEditing("new")}>
                  {addLabel ?? t("rp.add").replace("{title}", title)}
                </Button>
              ))}
        </fieldset>
      </Toolbar>

      {slow ? (
        // Between the toolbar and the table, where the eye already is after
        // pressing Search — and only once the wait is long enough to wonder
        // about. The rows below dim rather than disappear, so this says what is
        // happening to them.
        <LoadingNote action={waitingFor} subject={title} className="animate-note mb-4" />
      ) : null}

      {chips.length > 0 ? (
        // Applied filters are visible and removable here. A table that is
        // quietly hiding rows is a table that is lying to you.
        <fieldset disabled={busy} className="mb-4 flex flex-wrap items-center gap-2">
          {chips.map((chip) => (
            <FilterChip
              key={chip.name}
              label={chip.label}
              value={chip.value}
              onRemove={() => {
                setPage(1);
                // Removed, not blanked. A key left behind as `""` sends
                // nothing and shows no chip, so the filter reads as gone —
                // but a filters panel asking which conditions exist sees the
                // key and draws the row again, empty. Clearing one condition
                // has to leave the same state as never adding it.
                setFilterValues(({ [chip.name]: _cleared, ...rest }) => rest);
              }}
            />
          ))}
          <Button variant="ghost" size="sm" onClick={clearFilters}>
            {t("rp.clearFiltersBtn")}
          </Button>
        </fieldset>
      ) : null}

      {showFilters && renderFilters
        ? renderFilters(filterValues, applyFilters, () => setShowFilters(false))
        : null}
      {showFilters && !renderFilters && filters.length ? (
        <FiltersModal
          open={showFilters}
          filters={filters}
          values={filterValues}
          onApply={(next) => {
            setFilterValues(next);
            setPage(1);
            setShowFilters(false);
          }}
          onClose={() => setShowFilters(false)}
        />
      ) : null}

      {/* No `flex-1`: with `flex: 0 1 auto` the card is as tall as its rows and
          shrinks — never grows — to fit the frame. Stretched to fill, a list of
          one employee put six hundred pixels of nothing between the row and the
          pager. Full pages look exactly as before: the card hits the frame's
          height, the table scrolls inside it, the pager sits on the bottom. */}
      <Card className="flex min-h-0 flex-col overflow-hidden">
        {/* The table scrolls inside the card rather than the page, which is what
            lets the header stay put: `overflow-x: auto` forces `overflow-y`
            to `auto` too, so a sticky header needs this to be the scroller. The
            cap is generous, and a short table never reaches it. */}
        {/* The scroller. It was capped at a guess of how much chrome sits
            above it, which reserved height the table did not always use and left
            a band of nothing under the card. It takes exactly what is left now. */}
        {/* `relative` is load-bearing. Tailwind's `sr-only` is
            `position: absolute`, and the row menu puts one in every row. With
            no positioned ancestor they resolve against the document rather than
            this box, so row 25's screen-reader label landed at y=1400 and
            stretched <html> to 1441px — a page that scrolled through 568 pixels
            of nothing while every visible element fitted the screen. */}
        <div ref={scroller} className="relative min-h-0 flex-1 overflow-auto">
          <table className="w-full border-separate border-spacing-0 tabular-nums">
            <thead>
              <tr>
                {visibleColumns.map((column) => {
                  const sorted = Boolean(column.sortKey) && sort === column.sortKey;
                  return (
                    <th
                      key={column.key}
                      scope="col"
                      aria-sort={
                        sorted ? (order === "asc" ? "ascending" : "descending") : undefined
                      }
                      className={cn(
                        // Not uppercased. The reset zeroes `text-transform` on
                        // buttons, so a sortable header came out title case and
                        // its neighbours SHOUTED — and neither matched the name
                        // Customize Columns lists it under. The label as written
                        // is the one name for the column everywhere.
                        "text-fg-muted text-subhead border-line bg-surface-sunken sticky top-0 z-20 border-b px-4 py-3 text-left font-semibold whitespace-nowrap",
                        // The card's own gutter on the outside edges, so the
                        // first column starts where the card's padding would.
                        "first:pl-5 last:pr-5",
                        column.className,
                      )}
                    >
                      {column.sortKey ? (
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => sortBy(column.sortKey ?? "")}
                          className={cn(
                            "group/sort hover:text-fg -mx-1 flex w-full items-center gap-1.5 rounded px-1 transition-colors",
                            "disabled:pointer-events-none",
                            sorted && "text-fg",
                          )}
                        >
                          {column.label}
                          {sorted ? (
                            order === "asc" ? (
                              <ArrowUp className="text-accent size-3.5" aria-hidden />
                            ) : (
                              <ArrowDown className="text-accent size-3.5" aria-hidden />
                            )
                          ) : (
                            <ChevronsUpDown
                              className="text-fg-subtle size-3.5 opacity-0 transition-opacity group-hover/sort:opacity-100"
                              aria-hidden
                            />
                          )}
                        </button>
                      ) : (
                        column.label
                      )}
                    </th>
                  );
                })}
                {actions ? (
                  <th
                    scope="col"
                    className="border-line bg-surface-sunken sticky top-0 z-20 w-12 border-b px-4 py-3 last:pr-5"
                  >
                    <span className="sr-only">{t("rp.rowActions")}</span>
                  </th>
                ) : null}
              </tr>
            </thead>
            <tbody
              aria-busy={listing.isFetching || undefined}
              className={cn(
                "transition-opacity duration-[var(--duration-base)]",
                "[&_tr:not(:first-child)_td]:border-line [&_tr:not(:first-child)_td]:border-t",
                listing.isFetching && !listing.isPending && "opacity-55",
              )}
            >
              {rows.map((row) => (
                <tr
                  key={row.id}
                  className="hover:bg-surface-sunken/70 transition-colors duration-[var(--duration-fast)]"
                >
                  {visibleColumns.map((column) => (
                    <td
                      key={column.key}
                      className={cn(
                        "text-fg text-callout px-4 py-3.5 first:pl-5 last:pr-5",
                        column.className,
                      )}
                    >
                      {column.key === viewColumn && !noView ? (
                        // The id is the way in: click it to see the whole record.
                        <button
                          type="button"
                          onClick={() => void whole(row).then(open)}
                          // `block` and bounded: a button sized to its content
                          // gives a truncating child nothing to truncate against,
                          // so a long name pushed the column wide instead.
                          className="text-accent block max-w-full min-w-0 rounded text-left font-medium hover:underline"
                        >
                          {column.render(row, lookup, shown)}
                        </button>
                      ) : (
                        column.render(row, lookup, shown)
                      )}
                    </td>
                  ))}
                  {actions ? (
                    <td className="px-2 py-2 pr-3 text-right">
                      <RowMenu
                        disabled={busy}
                        onView={noView ? undefined : () => void whole(row).then(open)}
                        onEdit={
                          readOnly || noEdit || rowCanEdit?.(row, profile) === false
                            ? undefined
                            : () => setParam("edit", row.id)
                        }
                        onDelete={
                          readOnly || noDelete || rowCanDelete?.(row, profile) === false
                            ? undefined
                            : () => confirmDelete(row.id)
                        }
                        extra={rowActions?.(row, () => void invalidate())}
                      />
                    </td>
                  ) : null}
                </tr>
              ))}
              {showSkeleton
                ? Array.from({ length: 8 }, (_, index) => (
                    <tr key={index}>
                      {visibleColumns.map((column) => (
                        <td key={column.key} className="px-4 py-3.5 first:pl-5 last:pr-5">
                          <Skeleton
                            className="h-3.5"
                            style={{
                              width: `${45 + ((index * 17 + column.key.length * 7) % 40)}%`,
                            }}
                          />
                        </td>
                      ))}
                      {actions ? (
                        <td className="px-4 py-3.5 first:pl-5 last:pr-5">
                          <Skeleton className="ml-auto size-4 rounded-full" />
                        </td>
                      ) : null}
                    </tr>
                  ))
                : null}
              {listing.isError ? (
                <tr>
                  <td colSpan={visibleColumns.length + (actions ? 1 : 0)} className="p-0">
                    <EmptyState
                      kind="error"
                      title={t("rp.loadFailedTitle")}
                      // What actually went wrong, when the answer says: the
                      // server's own `detail` on a 500, "Cannot reach the
                      // server." when there was no answer at all. "Try again"
                      // is the right move for one of those and not the other,
                      // and a person cannot tell which from a generic line.
                      description={errorMessage(listing.error, t("rp.loadFailedBody"))}
                      action={
                        <Button variant="secondary" onClick={() => void listing.refetch()}>
                          <RotateCw aria-hidden /> {t("rp.retry")}
                        </Button>
                      }
                    />
                  </td>
                </tr>
              ) : null}
              {!listing.isPending && !listing.isError && rows.length === 0 ? (
                <tr>
                  <td colSpan={visibleColumns.length + (actions ? 1 : 0)} className="p-0">
                    {deferredSearch || activeFilters > 0 ? (
                      <EmptyState
                        kind="search"
                        title={t("rp.noMatch").replace("{plural}", plural.toLowerCase())}
                        description={t("rp.tryAnother")}
                        action={
                          activeFilters > 0 ? (
                            <Button variant="secondary" onClick={clearFilters}>
                              {t("rp.clearFiltersBtn")}
                            </Button>
                          ) : undefined
                        }
                      />
                    ) : (
                      <EmptyState
                        title={t("rp.noneYet").replace("{plural}", plural.toLowerCase())}
                        description={
                          readOnly
                            ? t("rp.entriesAppear")
                            : t("rp.firstAdd").replace("{singular}", singular.toLowerCase())
                        }
                        action={
                          readOnly || !canAdd
                            ? undefined
                            : (addMenu ?? (
                                <Button onClick={() => setEditing("new")}>
                                  {addLabel ?? t("rp.add").replace("{title}", title)}
                                </Button>
                              ))
                        }
                      />
                    )}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        {infinite ? (
          // No pager and no total: the next screenful comes when the table
          // reaches its end, and a log is not something one counts.
          feed.isFetchingNextPage ? (
            <div className="hairline-t text-fg-muted text-subhead shrink-0 px-4 py-3">
              {t("common.loading")}
            </div>
          ) : null
        ) : rows.length > 0 ? (
          <div className="hairline-t text-fg-muted text-subhead flex shrink-0 flex-wrap items-center justify-between gap-x-6 gap-y-3 px-4 py-3">
            <span className="flex items-center gap-2">
              {t("rp.showing")}
              <Select
                fieldSize="sm"
                aria-label={t("common.rowsPerPage")}
                value={size}
                onChange={(event) => {
                  setSize(Number(event.target.value));
                  setPage(1);
                }}
                className="w-auto"
              >
                {SIZES.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </Select>
              {t("common.of")} <span className="text-fg tabular-nums">{totalLabel}</span>
            </span>
            <span className="flex items-center gap-1">
              <PageButton
                label={t("common.firstPage")}
                disabled={busy || page === 1}
                onClick={() => setPage(1)}
              >
                <ChevronsLeft className="size-4" aria-hidden />
              </PageButton>
              <PageButton
                label={t("common.previousPage")}
                disabled={busy || page === 1}
                onClick={() => setPage(page - 1)}
              >
                <ChevronLeft className="size-4" aria-hidden />
              </PageButton>
              {pageWindow(page, pages).map((number) => (
                <PageButton
                  key={number}
                  label={t("rp.pageN").replace("{n}", String(number))}
                  active={number === page}
                  disabled={busy}
                  onClick={() => setPage(number)}
                >
                  {number}
                </PageButton>
              ))}
              {canJumpToEnd && pageWindow(page, pages).at(-1) !== pages && pages > 0 ? (
                <>
                  <span className="text-fg-subtle px-1" aria-hidden>
                    …
                  </span>
                  <PageButton
                    label={t("rp.pageN").replace("{n}", String(pages))}
                    disabled={busy}
                    onClick={() => setPage(pages)}
                  >
                    {pages}
                  </PageButton>
                </>
              ) : null}
              <PageButton
                label={t("common.nextPage")}
                disabled={busy || page === pages}
                onClick={() => setPage(page + 1)}
              >
                <ChevronRight className="size-4" aria-hidden />
              </PageButton>
              {/* Hidden rather than disabled where the jump is too deep: a
                  greyed button that never becomes available is furniture. */}
              {canJumpToEnd ? (
                <PageButton
                  label={t("common.lastPage")}
                  disabled={busy || page === pages}
                  onClick={() => setPage(pages)}
                >
                  <ChevronsRight className="size-4" aria-hidden />
                </PageButton>
              ) : null}
            </span>
          </div>
        ) : null}
      </Card>

      {pickingColumns ? (
        <ColumnPicker
          open
          columns={columns}
          hidden={hidden}
          onClose={() => setPickingColumns(false)}
          onApply={(next) => {
            setLocalHidden(next);
            writeJson(columnsKey, next);
            setPickingColumns(false);
          }}
        />
      ) : null}

      {confirmDialog}
    </Page>
  );
}

/** `member_since_from` reads as "Member since from" — good enough for a chip. */
const humanize = (key: string) =>
  key
    .replace(/_id$/, "")
    .replace(/_/g, " ")
    .replace(/^./, (c) => c.toUpperCase());

/** Up to five page numbers around the current one. */
function pageWindow(current: number, pages: number): number[] {
  const start = Math.max(1, Math.min(current - 2, pages - 4));
  return Array.from({ length: Math.min(5, pages) }, (_, index) => start + index);
}

/**
 * One page number. The current page is filled, so "where am I" is answered
 * before "where can I go".
 */
function PageButton({
  label,
  active,
  children,
  ...props
}: React.ComponentProps<"button"> & { label: string; active?: boolean }) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      aria-current={active ? "page" : undefined}
      className={cn(
        // Square at one digit and wider from there. It was a fixed 32px, which
        // a four-digit page number could not fit: 1677 spilled out of its own
        // button and into the two beside it.
        "text-subhead grid h-8 min-w-8 place-items-center rounded-lg px-2 tabular-nums",
        "transition-colors duration-[var(--duration-fast)]",
        "disabled:pointer-events-none disabled:opacity-35",
        active
          ? "bg-accent text-accent-fg font-medium"
          : "text-fg-muted hover:bg-surface-inset hover:text-fg",
      )}
      {...props}
    >
      {children}
    </button>
  );
}

function RowMenu({
  onView,
  onEdit,
  onDelete,
  extra = [],
  disabled = false,
}: {
  onView?: (() => void) | undefined;
  onEdit?: (() => void) | undefined;
  onDelete?: (() => void) | undefined;
  extra?: RowAction[] | undefined;
  /** Held while the list is being read: the row may be about to change. */
  disabled?: boolean;
}) {
  const t = useTranslation();
  // A popover lives in the top layer, so the table's overflow cannot clip it,
  // and light dismiss (outside click, Esc) is the browser's.
  const id = useId();
  const button = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);

  const place = () => {
    const anchor = button.current?.getBoundingClientRect();
    const panel = menu.current;
    if (!anchor || !panel) return;
    const width = 208;
    let left = anchor.right - width;
    let top = anchor.bottom + 6;
    if (top + panel.offsetHeight > window.innerHeight - 8)
      top = anchor.top - panel.offsetHeight - 6;
    if (left < 8) left = 8;
    Object.assign(panel.style, { top: `${top}px`, left: `${left}px`, width: `${width}px` });
  };
  const close = () => menu.current?.hidePopover();
  const pick = (action: () => void) => () => {
    close();
    action();
  };

  return (
    <>
      <button
        ref={button}
        type="button"
        popoverTarget={id}
        disabled={disabled}
        title={t("rp.rowActions")}
        className="text-fg-subtle hover:bg-surface-inset hover:text-fg grid size-8 place-items-center rounded-lg transition-colors disabled:pointer-events-none disabled:opacity-50"
      >
        <EllipsisVertical className="size-4" aria-hidden />
        <span className="sr-only">{t("rp.rowActions")}</span>
      </button>
      <div
        ref={menu}
        id={id}
        popover="auto"
        onToggle={(event) => event.newState === "open" && place()}
        className="border-line bg-surface-raised animate-sheet fixed m-0 rounded-xl border p-1 shadow-[var(--shadow-float)]"
      >
        {onView ? (
          <button type="button" onClick={pick(onView)} className={menuItemClass()}>
            <Eye aria-hidden />
            {t("rp.view")}
          </button>
        ) : null}
        {onEdit ? (
          <button type="button" onClick={pick(onEdit)} className={menuItemClass()}>
            <Pencil aria-hidden />
            {t("common.edit")}
          </button>
        ) : null}
        {extra.map((action) => (
          <button
            key={action.label}
            type="button"
            onClick={pick(action.run)}
            className={menuItemClass(action.danger)}
          >
            {action.label}
          </button>
        ))}
        {onDelete ? (
          <>
            {onView || onEdit || extra.length ? <MenuSeparator /> : null}
            <button type="button" onClick={pick(onDelete)} className={menuItemClass(true)}>
              <Trash2 aria-hidden />
              {t("common.delete")}
            </button>
          </>
        ) : null}
      </div>
    </>
  );
}

/** Every field of the row, as the API returned it. Ids resolve to names where a filter knows them. */
const AUDIT_KEYS = ["created_at", "created_by_id", "updated_at", "updated_by_id"] as const;

/** The footnote's two lines: a stamp is a time and the person who caused it. */
const AUDIT_EVENTS = [
  { at: "created_at", by: "created_by_id", verb: "rp.stampCreated" },
  { at: "updated_at", by: "updated_by_id", verb: "rp.stampUpdated" },
] as const;

/**
 * What kind of value a field holds, said before it is read.
 *
 * Keyed off the value at hand rather than a schema, because the detail view has
 * no schema — it is handed whatever the API returned. Tones come from the
 * app's own semantic tokens, so they hold in both themes.
 */
function fieldIcon(key: string, value: unknown): { component: LucideIcon; tone: string } {
  if (key.endsWith("_by_id") || key === "manager_id")
    return { component: UserRound, tone: "text-accent" };
  if (/_at$/.test(key)) return { component: CalendarDays, tone: "text-success" };
  if (/_date$/.test(key)) return { component: CalendarDays, tone: "text-success" };
  if (key.endsWith("_id")) return { component: Link2, tone: "text-accent" };
  if (typeof value === "boolean") return { component: ToggleLeft, tone: "text-fg-subtle" };
  if (typeof value === "number") return { component: Hash, tone: "text-warning" };
  return { component: CaseSensitive, tone: "text-accent" };
}

/** Who, or when — the two things the record's footnote says. */
const AUDIT_ICON: Record<string, LucideIcon> = {
  created_at: CalendarPlus,
  created_by_id: UserRound,
  updated_at: Clock,
  updated_by_id: UserPen,
};

export function DetailPanel<T extends { id: number }>({
  singular,
  row,
  lookup,
  onClose,
  onRefresh,
  busy,
  onEdit,
  onDelete,
  extra,
  hide,
  sections,
  labels,
  actions,
}: {
  singular: string;
  row: T;
  lookup: Lookup;
  onClose: () => void;
  onRefresh: () => void;
  busy: boolean;
  /** Absent when the screen or the person may not change this record. */
  onEdit?: (() => void) | undefined;
  onDelete?: (() => void) | undefined;
  extra?: ReactNode;
  /** Shown better by `extra`, so kept out of the list above it. */
  hide?: string[] | undefined;
  sections?: { title: string; fields?: string[]; render?: (row: T) => ReactNode }[] | undefined;
  labels?: Record<string, string> | undefined;
  /** The row menu's own actions, offered here too — a record read is a record acted on. */
  actions?: RowAction[] | undefined;
}) {
  const t = useTranslation();
  const record = row as unknown as Record<string, unknown>;
  // `province_name` is the label of `province_id` and reads as "Province";
  // `user_name` and `full_name` are names in their own right, and stripping the
  // suffix from those left the fields labelled "User" and "Full".
  const label = (key: string) =>
    labels?.[key] ??
    key
      .replace(/_id$/, "")
      .replace(/_name$/, (match) => (`${key.slice(0, -5)}_id` in record ? "" : match))
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
  const show = (key: string, value: unknown): ReactNode => {
    if (value === null || value === undefined || value === "")
      return <span className="text-fg-subtle">—</span>;
    // An object or a list has no one-line form; `String(it)` is "[object
    // Object]". Such fields are dropped from the list below, so this is the
    // backstop for one that slips through rather than the place they are handled.
    if (typeof value === "object") return <span className="text-fg-subtle">—</span>;
    // Every id, not just the authorship pair: the lookup already answers for
    // any declared `fk` filter and falls back to the id itself, so a club, a
    // city or a promo reads as its name wherever the screen filters on one.
    if (key.endsWith("_id"))
      return lookup(key === "updated_by_id" ? "created_by_id" : key, value as number);
    if (/_at$/.test(key) && typeof value === "string") return fmtDate(value);
    // A plain date has no time to print; `2026-08-30` is not how anyone reads one.
    if (/_date$/.test(key) && typeof value === "string")
      return format(new Date(value), "MM/dd/yyyy");
    if (typeof value === "boolean") return value ? t("rp.yes") : t("rp.no");
    if (typeof value === "number") return value.toLocaleString();
    return String(value);
  };

  // The name leads; the id is a badge; a parent's id gives way to its name.
  // Not every table calls it `name` — an account's is `full_name`, and failing
  // that the number it signs in with.
  const titleKey = ["name", "full_name", "title", "user_name"].find(
    (key) => typeof record[key] === "string" && record[key] !== "",
  );
  const title = titleKey ? (record[titleKey] as string) : null;
  // Delayed, so a re-read that lands in 80ms shows nothing rather than a
  // flicker; the dim and the held actions follow `busy` directly, because a
  // control that is live for 300ms is a control somebody can press.
  const rereading = useDelayed(busy);
  const details = Object.entries(record).filter(
    ([key, value]) =>
      key !== "id" &&
      key !== titleKey &&
      !hide?.includes(key) &&
      !(AUDIT_KEYS as readonly string[]).includes(key) &&
      // An id whose label is also on the record: `promo_code_id` beside
      // `promo_code`, `club_id` beside `club_name`. The name is the row; the
      // id was a second row saying "7".
      !(
        key.endsWith("_id") &&
        (`${key.slice(0, -3)}_name` in record || key.slice(0, -3) in record)
      ) &&
      // A club, a posting, a list of groups or of photos: a record within the
      // record. It reads as "[object Object]" in a row, so the screen shows it
      // through `renderDetailExtra` or not at all.
      (value === null || typeof value !== "object"),
  );

  /**
   * The fields under the headings the screen named, in that order, with
   * everything it did not name in a last untitled group. No headings at all
   * means one list, exactly as before.
   */
  const groups = (() => {
    if (!sections?.length) return [];
    const taken = new Set<string>();
    const named = sections
      .map((section) => {
        const entries = details.filter(([key]) => {
          if (!section.fields?.includes(key)) return false;
          taken.add(key);
          return true;
        });
        return { title: section.title, entries, body: section.render?.(row) };
      })
      .filter((group) => group.entries.length > 0 || group.body);
    const rest = details.filter(([key]) => !taken.has(key));
    return rest.length ? [...named, { title: "", entries: rest, body: undefined }] : named;
  })();

  /**
   * A property list: a type icon, the label, then the value in its own column,
   * two fields across.
   *
   * The icon says what kind of thing the value is before it is read — text, a
   * count, a day, a person — and it gives every row a fixed left edge, so the
   * labels and the values each line up down their own column. Two earlier tries
   * put the value at the far end of a full-width row; across a page this wide
   * that was a long way to travel for one number.
   *
   * A value too long for its column takes the whole row rather than wrapping
   * into a narrow ribbon.
   */
  const list = (entries: [string, unknown][], columns = 1) => (
    <dl className={cn("grid gap-x-12 gap-y-1", columns === 2 && "md:grid-cols-2")}>
      {entries.map(([key, value]) => {
        const Icon = fieldIcon(key, value);
        const wide = typeof value === "string" && value.length > 40;
        return (
          <div
            key={key}
            className={cn(
              "grid grid-cols-[1rem_minmax(0,10rem)_minmax(0,1fr)] items-baseline gap-x-3 py-2",
              wide && "md:col-span-2",
            )}
          >
            <Icon.component
              className={cn("size-4 shrink-0 translate-y-0.5", Icon.tone)}
              aria-hidden
            />
            <dt className="text-fg-muted text-callout truncate">{label(key)}</dt>
            <dd className="text-fg text-callout min-w-0 break-words">{show(key, value)}</dd>
          </div>
        );
      })}
    </dl>
  );

  return (
    <>
      <PageHeader
        back={
          <button type="button" onClick={onClose} className={backLinkClass}>
            <BackArrow label={singular} />
          </button>
        }
        eyebrow={singular}
        title={
          <span className="flex flex-wrap items-center gap-2.5">
            <span className="min-w-0 truncate">{title ?? `${singular} ${row.id}`}</span>
            <span className="bg-surface-inset text-fg-muted text-footnote rounded-full px-2 py-0.5 font-medium tabular-nums">
              {t("rp.id")} {row.id}
            </span>
          </span>
        }
        actions={
          // Held while the record is being re-read, like a list's toolbar. Not
          // the back link, which is outside this: leaving is always allowed.
          <fieldset disabled={busy} className="contents">
            <Button variant="secondary" aria-busy={busy || undefined} onClick={onRefresh}>
              <RotateCw className={cn(busy && "animate-spin")} aria-hidden />
              {t("rp.refresh")}
            </Button>
            {/* The same actions the row menu offers, so a record read here can
                be acted on here rather than closed and found again in the list. */}
            {actions?.map((action) => (
              <Button
                key={action.label}
                variant={action.danger ? "danger" : "secondary"}
                onClick={action.run}
              >
                {action.label}
              </Button>
            ))}
            {onDelete ? (
              // Destructive, and it should look it: it read as one more grey
              // button beside Refresh.
              <Button variant="danger" onClick={onDelete}>
                <Trash2 aria-hidden />
                {t("common.delete")}
              </Button>
            ) : null}
            {onEdit ? (
              <Button onClick={onEdit}>
                <Pencil aria-hidden />
                {t("common.edit")}
              </Button>
            ) : null}
          </fieldset>
        }
      />

      {/* What the wait is for, said above the record it is about. Delayed, so a
          re-read that lands in 80ms shows nothing rather than a flicker. */}
      {rereading ? <LoadingNote className="mb-3" /> : null}

      {/* The record's own fields, two columns wide where there is room. The
          audit trail closes the same card rather than opening a second one: it
          is a footnote about this record, not a section beside it. */}
      <section
        aria-busy={busy || undefined}
        className={cn(
          "bg-surface-raised rounded-card border-line border",
          "transition-opacity duration-[var(--duration-base)]",
          busy && "opacity-55",
        )}
      >
        {/* Grouped where the screen says how, one list where it does not. The
            record's own history stays after all of it, in the footer. */}
        {groups.length ? (
          <div className="divide-line divide-y">
            {groups.map((group) => (
              <div key={group.title} className="px-6 py-5">
                {group.title ? (
                  <h3 className="text-fg-subtle text-eyebrow mb-3 tracking-[0.04em] uppercase">
                    {group.title}
                  </h3>
                ) : null}
                {group.body}
                {group.entries.length ? list(group.entries, 2) : null}
              </div>
            ))}
          </div>
        ) : (
          <div className="px-6 py-5">{list(details, 2)}</div>
        )}

        {/* Four labelled cells for two facts read as a table the record does not
            need. One line each — when, then who — is the footnote it is. */}
        <footer className="hairline-t bg-surface-sunken/40 text-subhead px-6 py-3">
          <dl className="text-fg-subtle grid gap-x-12 gap-y-1.5 sm:grid-cols-2">
            {AUDIT_EVENTS.filter(({ at }) => at in record).map(({ at, by, verb }) => {
              const Icon = AUDIT_ICON[at];
              return (
                <div key={at} className="min-w-0">
                  <dt className="flex items-center gap-1.5">
                    {Icon ? <Icon className="size-3.5 shrink-0" aria-hidden /> : null}
                    {t(verb)}
                  </dt>
                  <dd className="text-fg-muted mt-0.5 min-w-0 break-words">
                    {show(at, record[at])}
                    {record[by] ? (
                      <>
                        <span className="text-fg-subtle"> · </span>
                        {show(by, record[by])}
                      </>
                    ) : null}
                  </dd>
                </div>
              );
            })}
          </dl>
        </footer>
      </section>

      {extra ? (
        <div className="bg-surface-raised rounded-card border-line mt-4 border px-5 py-4">
          {extra}
        </div>
      ) : null}
    </>
  );
}

/** Customize Columns: toggles, applied on OK. */
function ColumnPicker<T>({
  open,
  columns,
  hidden,
  onClose,
  onApply,
}: {
  open: boolean;
  columns: ColumnDef<T>[];
  hidden: string[];
  onClose: () => void;
  onApply: (hidden: string[]) => void;
}) {
  const t = useTranslation();
  // Mounted only while open, so the draft starts from what is on screen every
  // time without an effect to reset it.
  const [draft, setDraft] = useState<string[]>(hidden);
  const shown = columns.length - draft.length;
  // A–Z by label, not in table order. This list is read to *find* a column —
  // "is Employee Number in here?" — and table order is no help with that once
  // there are thirty of them. The table itself keeps its own order; only the
  // picker is sorted. `localeCompare` so an accented label files where a
  // reader expects it rather than where its code point falls.
  const listed = [...columns].sort((a, b) => a.label.localeCompare(b.label));

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="sm"
      title={t("rp.customizeColumn")}
      description={t("rp.showHide")}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button onClick={() => onApply(draft)} disabled={shown === 0}>
            {t("rp.ok")}
          </Button>
        </>
      }
    >
      <ul className="grid gap-1">
        {listed.map((column) => {
          const on = !draft.includes(column.key);
          return (
            <li key={column.key}>
              <label className="hover:bg-surface-sunken flex cursor-pointer items-center gap-3 rounded-lg px-2 py-2">
                <Checkbox
                  checked={on}
                  // The last visible column cannot be turned off: a table with
                  // no columns is not a smaller table, it is a broken one.
                  disabled={on && shown === 1}
                  onChange={(event) =>
                    setDraft((d) =>
                      event.target.checked
                        ? d.filter((k) => k !== column.key)
                        : [...d, column.key],
                    )
                  }
                />
                <span className="text-fg text-callout">{column.label}</span>
              </label>
            </li>
          );
        })}
      </ul>
    </Modal>
  );
}

/** Filters as a dialog: a draft the person can change freely, applied on one click. */
/**
 * A range is two filters that are one question.
 *
 * `created_from`/`created_to` and `min_population`/`max_population` were two
 * rows apiece, each labelled with half a thought — "Created from", then
 * "Created to" — so a drawer of five questions read as eight controls. Paired,
 * the stem is the label and the two ends sit side by side under it.
 */
function rangePair(filter: FilterDef, next: FilterDef | undefined) {
  if (!next) return null;
  const pairs: [RegExp, RegExp][] = [
    [/_from$/, /_to$/],
    [/^min_/, /^max_/],
  ];
  for (const [head, tail] of pairs) {
    if (!head.test(filter.name) || !tail.test(next.name)) continue;
    const stem = filter.name.replace(head, "").replace(/^_|_$/g, "");
    if (next.name.replace(tail, "").replace(/^_|_$/g, "") !== stem) continue;
    return stem;
  }
  return null;
}

/** Which end of a range a field is: "From"/"To", or "Minimum"/"Maximum". */
function rangeEnd(name: string): string {
  if (name.endsWith("_from")) return "From";
  if (name.endsWith("_to")) return "To";
  if (name.startsWith("min_")) return "Minimum";
  if (name.startsWith("max_")) return "Maximum";
  return "";
}

function FiltersModal({
  open,
  filters,
  values,
  onApply,
  onClose,
}: {
  open: boolean;
  filters: FilterDef[];
  /** Every field here is one value; the type is the page's, so it is the union. */
  values: FilterValues;
  onApply: (values: FilterValues) => void;
  onClose: () => void;
}) {
  const t = useTranslation();
  // Mounted only while open, so this starts from the applied filters each time.
  const [draft, setDraft] = useState(values);

  const set = (name: string, value: string) =>
    setDraft((current) => {
      const next = { ...current, [name]: value };
      // A new parent invalidates whatever child was chosen under the old one.
      for (const child of filters) if (child.dependsOn === name) next[child.name] = "";
      return next;
    });

  const at = (values: FilterValues, name: string) => String(values[name] ?? "");
  const chosen = filters.filter((filter) => at(draft, filter.name) !== "").length;
  // Nothing to apply until something differs from what is already applied — the
  // button used to invite a round trip that could only return the same rows.
  const changed = filters.some((filter) => at(draft, filter.name) !== at(values, filter.name));

  // Ranges take both of their halves, so the second one is not drawn again.
  const paired = new Set<string>();
  const rows: { key: string; label: string; parts: FilterDef[] }[] = [];
  filters.forEach((filter, index) => {
    if (paired.has(filter.name)) return;
    const next = filters[index + 1];
    const stem = rangePair(filter, next);
    if (stem && next) {
      paired.add(next.name);
      rows.push({ key: filter.name, label: humanize(stem), parts: [filter, next] });
    } else {
      rows.push({ key: filter.name, label: filter.label, parts: [filter] });
    }
  });

  return (
    // A modal that is as tall as its questions, not a sheet down the whole edge
    // of the screen: five filters left two thirds of a full-height drawer
    // empty, and put the Apply button a long way from the last field.
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={
        <span className="flex items-baseline gap-2">
          {t("rp.filters")}
          {chosen > 0 ? (
            <span className="bg-accent-soft text-accent text-footnote rounded-full px-2 py-0.5 font-medium tabular-nums">
              {chosen}
            </span>
          ) : null}
        </span>
      }
      footer={
        <>
          <Button
            variant="ghost"
            className="mr-auto"
            disabled={chosen === 0}
            onClick={() => setDraft({})}
          >
            {t("rp.clear")}
          </Button>
          <Button variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button disabled={!changed} onClick={() => onApply(draft)}>
            {t("rp.applyFilter")}
          </Button>
        </>
      }
    >
      {/* One question per line, so the modal reads straight down; a range still
          keeps its two ends side by side inside its own row. */}
      <div className="grid gap-4 pb-3">
        {rows.map((row) => {
          const active = row.parts.some((part) => at(draft, part.name) !== "");
          return (
            <div key={row.key} className="grid gap-1.5">
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-fg text-subhead font-medium">{row.label}</span>
                {/* One filter at a time, without wiping the other four. */}
                {active ? (
                  <button
                    type="button"
                    onClick={() => row.parts.forEach((part) => set(part.name, ""))}
                    className="text-fg-subtle hover:text-accent text-footnote"
                  >
                    {t("rp.clear")}
                  </button>
                ) : null}
              </div>
              {/* The two ends of a range side by side, each captioned, so the
                  pair reads as one question rather than two rows that happen to
                  share a heading. */}
              <div className={cn(row.parts.length === 2 && "grid grid-cols-2 gap-2")}>
                {row.parts.map((part) => (
                  <div key={part.name} className="grid gap-1">
                    {row.parts.length === 2 ? (
                      <span className="text-fg-muted text-footnote">{rangeEnd(part.name)}</span>
                    ) : null}
                    <FilterControl
                      filter={part}
                      value={at(draft, part.name)}
                      parent={part.dependsOn ? at(draft, part.dependsOn) : ""}
                      onChange={(value) => set(part.name, value)}
                    />
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </Modal>
  );
}

function FilterControl({
  filter,
  value,
  parent,
  onChange,
}: {
  filter: FilterDef;
  value: string;
  parent: string;
  onChange: (value: string) => void;
}) {
  const t = useTranslation();
  const waiting = Boolean(filter.dependsOn) && parent === "";
  const options = useQuery({
    queryKey: ["options", filter.name, parent],
    queryFn: ({ signal }) =>
      filter.loadOptions?.(parent, signal) ?? Promise.resolve(filter.options ?? []),
    enabled: filter.type === "select" && !waiting,
    staleTime: 5 * 60_000,
  });

  return (
    <label className="grid gap-1.5">
      {/* A `<label>` names the control it wraps by its own text. The visible
          text moved to the row above, which names nothing — a label is not a
          widget, and `aria-label` on one does not reach the control inside — so
          the name is carried here. For a range this is also the only thing that
          says which end you are in. */}
      <span className="sr-only">{filter.label}</span>
      {filter.type === "fk" ? (
        <FkSelect
          endpoint={filter.endpoint ?? ""}
          value={value}
          onChange={(next) => onChange(next)}
          params={
            filter.dependsOn ? { [filter.parentParam ?? filter.dependsOn]: parent } : undefined
          }
          disabled={waiting}
          placeholder={
            waiting
              ? t("rp.chooseFirst").replace(
                  "{parent}",
                  filter.dependsOn?.replace(/_id$/, "") ?? "",
                )
              : t("rp.selectDots")
          }
        />
      ) : filter.type === "number" || filter.type === "date" || filter.type === "text" ? (
        <Input
          type={filter.type}
          inputMode={filter.type === "number" ? "numeric" : undefined}
          value={value}
          onChange={(event) => onChange(event.target.value)}
        />
      ) : (
        // Ours, not the operating system's: a native <select> opens the OS menu,
        // which on macOS is dark grey with its own type and its own blue — the
        // one control that fell out of the app the moment it was opened.
        <OptionSelect
          value={value}
          onChange={onChange}
          disabled={waiting}
          placeholder={
            waiting
              ? t("rp.chooseFirst").replace(
                  "{parent}",
                  filter.dependsOn?.replace(/_id$/, "") ?? "",
                )
              : t("rp.selectDots")
          }
          choices={
            filter.type === "boolean"
              ? [
                  { value: "true", label: t("rp.yes") },
                  { value: "false", label: t("rp.no") },
                ]
              : (options.data ?? []).map((option) => ({
                  value: String(option.value),
                  label: option.label,
                }))
          }
        />
      )}
    </label>
  );
}

/**
 * One property, in the shape the detail view draws every other one: the type
 * icon, the label, then the value in its own column.
 *
 * Exported so a section that draws itself — a club and its posting, a role —
 * does not end up looking like a different screen three rows down.
 */
export function DetailProperty({
  label,
  icon,
  children,
}: {
  label: string;
  /** Defaults to the text icon; pass one where the value is not text. */
  icon?: LucideIcon;
  children: ReactNode;
}) {
  const Icon = icon ?? CaseSensitive;
  return (
    <div className="grid grid-cols-[1rem_minmax(0,10rem)_minmax(0,1fr)] items-baseline gap-x-3 py-2">
      <Icon className="text-accent size-4 shrink-0 translate-y-0.5" aria-hidden />
      <dt className="text-fg-muted text-callout truncate">{label}</dt>
      <dd className="text-fg text-callout min-w-0 break-words">{children}</dd>
    </div>
  );
}

/**
 * The chrome a create or edit wears when it takes over the page: a way back, a
 * title, and the form in a card that runs the page's width.
 *
 * Exported because three screens cannot express their form as a `FieldDef[]` —
 * an employee number that gates the rest of the form, a listing's photo tray —
 * and they should still be indistinguishable from the ones that can.
 */
export function FormPanel({
  title,
  onClose,
  actions,
  children,
}: {
  title: string;
  onClose: () => void;
  actions: ReactNode;
  children: ReactNode;
}) {
  const t = useTranslation();
  return (
    <section>
      <button
        type="button"
        onClick={onClose}
        className="text-fg-muted hover:text-fg text-callout inline-flex items-center gap-2"
      >
        <ChevronLeft className="size-4" aria-hidden /> {t("rp.back")}
      </button>
      <h2 className="text-title-2 text-heading mt-3">{title}</h2>
      {/* In a card, like the role editor's, and taking the page's width: the
          fields were a single narrow column with the right half of the screen
          left blank beside them. */}
      <div className="bg-surface-raised rounded-card mt-5 p-5 shadow-[var(--shadow-lift)]">
        {children}
        <div className="mt-6 flex items-center justify-end gap-3">{actions}</div>
      </div>
    </section>
  );
}

export function EditDialog({
  singular,
  endpoint,
  fields,
  initial,
  id,
  stamp = null,
  onClose,
  onSaved,
  columns = 1,
  variant = "modal",
  fixed,
  action,
}: {
  singular: string;
  endpoint: string;
  fields: FieldDef[];
  initial: Record<string, string>;
  id: number | null;
  /**
   * A sub-route of the record to PATCH — `basic-info` — for a screen that edits
   * one card of a record at a time, each card its own endpoint.
   */
  action?: string | undefined;
  /** The record's `updated_at` as read, sent back so a save over someone else's is refused. */
  stamp?: string | null | undefined;
  onClose: () => void;
  onSaved: () => void;
  columns?: 1 | 2;
  /**
   * "modal" is a sheet over the screen; "panel" takes the place of the list it
   * was opened from, the way the role editor does. Same form either way — only
   * the chrome around it differs.
   */
  variant?: "modal" | "panel";
  /**
   * Sent with every save but never shown: the parent a row belongs to. A
   * reason's kind is decided by the list it was opened from, so it is neither
   * a field to fill in nor something to leave out of the body.
   */
  fixed?: Record<string, unknown>;
}) {
  const t = useTranslation();
  // A literal id, not `useId`: only one edit dialog is ever open, and the
  // footer's submit button reaches the form across the modal by this name.
  const formId = "resource-edit-form";
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(fields.map((field) => [field.name, initial[field.name] ?? ""])),
  );
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      id === null
        ? createResource(endpoint, body)
        : updateResource(endpoint, id, body, stamp, action),
    onSuccess: () => {
      toast.success(
        t(id === null ? "rp.created" : "rp.updated").replace("{singular}", singular),
      );
      onSaved();
    },
    onError: (err) =>
      setError(
        errorMessage(err, t("rp.saveFailed").replace("{singular}", singular.toLowerCase())),
      ),
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    // Numbers go over as numbers; a cleared optional field goes over as null
    // so an update can actually unset it. A select's value is a number only
    // when it looks like one — an id — and a word otherwise: `gender` is
    // "female", and `Number("female")` is NaN, which JSON writes as null.
    const body = Object.fromEntries(
      fields.map((field) => {
        const raw = values[field.name] ?? "";
        if (field.type === "boolean") return [field.name, raw === "true"];
        if (raw === "") return [field.name, null];
        const numeric =
          field.type === "number" || (field.type === "select" && /^-?\d+$/.test(raw));
        return [field.name, numeric ? Number(raw) : raw];
      }),
    );
    save.mutate({ ...body, ...fixed });
  };

  const title = t(id === null ? "rp.addSingular" : "rp.editSingular").replace(
    "{singular}",
    singular,
  );
  const actions = (
    <>
      <Button type="button" variant="secondary" onClick={onClose}>
        {t("common.cancel")}
      </Button>
      <Button
        type="submit"
        form={formId}
        loading={save.isPending}
        loadingLabel={t("common.saving")}
      >
        {t("rp.save")}
      </Button>
    </>
  );

  // A panel has the page's width, so its fields run two across whatever the
  // caller asked for; the section headings and textareas need to know too.
  const across = columns === 2 || variant === "panel" ? 2 : 1;
  const body = (
    <form
      id={formId}
      onSubmit={submit}
      className={cn("grid gap-4 pb-2", across === 2 && "md:grid-cols-2 md:items-start")}
    >
      {/* The error sits at the top of the form, where the eye goes when a
            save fails, rather than at the bottom under the fold. */}
      {error ? (
        <p
          role="alert"
          className="bg-danger-soft text-danger text-callout flex items-start gap-2 rounded-lg px-3 py-2.5 md:col-span-2"
        >
          <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden />
          <span>{error}</span>
        </p>
      ) : null}

      {fields.map((field, index) => (
        <FieldInput
          key={field.name}
          field={field}
          editing={id !== null}
          columns={across}
          sectionStart={
            field.section !== undefined && field.section !== fields[index - 1]?.section
          }
          value={values[field.name] ?? ""}
          parent={field.dependsOn ? (values[field.dependsOn] ?? "") : ""}
          onChange={(value) =>
            setValues((current) => {
              const next = { ...current, [field.name]: value };
              // A new parent invalidates whatever child was chosen under the old one.
              for (const child of fields)
                if (child.dependsOn === field.name) next[child.name] = "";
              return next;
            })
          }
        />
      ))}
    </form>
  );

  if (variant === "panel") {
    return (
      <FormPanel title={title} onClose={onClose} actions={actions}>
        {body}
      </FormPanel>
    );
  }

  return (
    <Modal
      open
      onClose={onClose}
      size={columns === 2 ? "lg" : "md"}
      title={title}
      footer={actions}
    >
      {body}
    </Modal>
  );
}

function FieldInput({
  field,
  value,
  parent,
  onChange,
  editing = false,
  columns = 1,
  sectionStart = false,
}: {
  field: FieldDef;
  value: string;
  parent: string;
  onChange: (value: string) => void;
  editing?: boolean;
  columns?: 1 | 2;
  sectionStart?: boolean;
}) {
  const t = useTranslation();
  const options = useQuery({
    queryKey: ["options", field.name, parent],
    queryFn: ({ signal }) =>
      field.loadOptions?.(parent, signal) ?? Promise.resolve(field.options ?? []),
    enabled: field.type === "select" && (!field.dependsOn || parent !== ""),
    staleTime: 5 * 60_000,
  });

  const disabled = field.disabled || (editing && field.lockOnEdit);
  // A section heading groups the fields under it; the first one needs no gap
  // above it, the rest do, so the form reads as parts rather than a list.
  const heading = sectionStart ? (
    <h3
      className={cn(
        "text-eyebrow text-fg-subtle hairline-b pt-3 pb-2 first:pt-0",
        columns === 2 && "md:col-span-2",
      )}
    >
      {field.section}
    </h3>
  ) : null;

  let control: ReactNode;
  // A paragraph takes the whole row; a name or a number does not need to.
  let span = false;
  if (field.type === "fk") {
    const waiting = Boolean(field.dependsOn) && parent === "";
    control = (
      <FkSelect
        id={field.name}
        endpoint={field.endpoint ?? ""}
        value={value}
        onChange={(next) => onChange(next)}
        params={
          field.dependsOn ? { [field.parentParam ?? field.dependsOn]: parent } : undefined
        }
        disabled={disabled || waiting}
        required={field.required}
        placeholder={
          waiting
            ? t("rp.chooseFirst").replace("{parent}", field.dependsOn ?? "")
            : t("rp.select").replace("{label}", field.label.toLowerCase())
        }
      />
    );
  } else if (field.type === "select") {
    control = (
      <Select
        id={field.name}
        required={field.required}
        value={value}
        disabled={disabled || (Boolean(field.dependsOn) && parent === "")}
        onChange={(event) => onChange(event.target.value)}
        placeholder={
          field.dependsOn && parent === ""
            ? t("rp.chooseFirst").replace("{parent}", field.dependsOn ?? "")
            : t("rp.select").replace("{label}", field.label.toLowerCase())
        }
      >
        {(options.data ?? []).map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </Select>
    );
  } else if (field.type === "boolean") {
    control = (
      <span className="flex h-10 items-center">
        <Toggle
          id={field.name}
          disabled={disabled}
          checked={value === "true"}
          onChange={(event) => onChange(event.target.checked ? "true" : "false")}
        />
      </span>
    );
  } else if (field.type === "image") {
    control = (
      <label className="border-line-strong text-fg-muted text-subhead hover:border-accent hover:bg-accent-soft/40 rounded-card flex min-h-32 cursor-pointer flex-col items-center justify-center gap-2 border border-dashed p-4 transition-colors">
        {value ? (
          <img src={value} alt="" className="max-h-28 rounded-lg object-contain" />
        ) : null}
        <span>{value ? t("rp.changeImage") : t("rp.dropImage")}</span>
        <input
          type="file"
          accept="image/*"
          className="sr-only"
          disabled={disabled}
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = "";
            if (file) void readImage(file, 800).then(onChange);
          }}
        />
      </label>
    );
  } else if (field.type === "file") {
    // Any document, kept as a data URL like the images; no resize, so the cap is the API's.
    control = (
      <label className="border-line-strong text-fg-muted text-subhead hover:border-accent hover:bg-accent-soft/40 rounded-card flex min-h-32 cursor-pointer flex-col items-center justify-center gap-2 border border-dashed p-4 transition-colors">
        <Upload className="size-5" aria-hidden />
        <span>
          {value
            ? t("rp.fileAttached").replace(
                "{kb}",
                String(Math.round((value.length * 3) / 4 / 1024)),
              )
            : t("rp.dropFile")}
        </span>
        <input
          type="file"
          className="sr-only"
          disabled={disabled}
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = "";
            if (!file) return;
            const reader = new FileReader();
            reader.onload = () => onChange(String(reader.result ?? ""));
            reader.readAsDataURL(file);
          }}
        />
      </label>
    );
  } else if (field.type === "textarea") {
    span = columns === 2;
    control = (
      <Textarea
        id={field.name}
        required={field.required}
        rows={3}
        disabled={disabled}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    );
  } else {
    control = (
      <Input
        id={field.name}
        type={field.type ?? "text"}
        required={field.required}
        step={field.step}
        min={field.min}
        max={field.max}
        disabled={disabled}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    );
  }

  return (
    <>
      {heading}
      <Field
        label={field.label}
        htmlFor={field.name}
        required={field.required}
        className={cn(span && "md:col-span-2")}
      >
        {control}
      </Field>
    </>
  );
}
